import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type CSSProperties,
} from 'react';
import { Alert, Button, Input, Spin } from 'antd';
import { Check, Paperclip, Redo2, Undo2, X } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { AIHistoryPageIcon } from '@/assets/icons/icons';
import type { TextTemplateChunk } from '@/proto/logiblocks/logiblocks';
import useToken from '@/common/helper/useToken';
import { GetUsersByTeamIds } from '@/apis/settings';
import {
  sendAgentCorrection,
  sendAgentAnswer,
  sendAgentSegmentReceipt,
  cancelAgentDraft,
  listAgentSessions,
  startAgentSession,
  streamAgentMessage,
  validateAgentGraph,
  AgentApiError,
  type AgentAttachmentMeta,
  type AgentChatTurnResponse,
  type AgentConfigurationNodeState,
  type AgentConversationTurn,
  type AgentDraftProtocolVersion,
  type AgentStructuredQuestion,
  type ValidateAgentGraphResponse,
} from '@/apis/logiblocksAgentAPI';
import {
  WorkflowGraphApiError,
  type WorkflowGraphWriteResult,
} from '@/apis/logiblocksAPI';
import {
  lbWorkflowToBase64,
  reactFlowToLBWorkflow,
} from '@/pages/Logiblocks/serialization';
import type { ReactFlowEdge, ReactFlowNode } from '@/pages/Logiblocks/types';
import { applyGraphOps } from './applyGraphOps';
import { bindAuthoringSchema, type AuthoringSchema } from './authoringSchema';
import {
  parseCompilerRawOps,
  parseRawOps,
  readGraphOpsVersion,
  RawOpParseError,
} from './graphOps';
import { describeExistingNode, describeStep } from './opDescriptions';
import { groupOpsIntoSteps } from './planPhases';
import { useAgentCheckpoints } from './useAgentCheckpoints';
import { planRevealStepDelayMs, type RevealStepInput } from './usePlanReveal';
import { isPostAnswerPreparationQuestionCurrent } from './preparationJoin';
import styles from './WorkflowAgentChatPanel.module.css';
import { ClarifyingQuestionCard } from './ClarifyingQuestionCard';
import { composerAnswerValues } from './questionAnswers';
import { CopilotLatencyTrace } from './copilotLatency';
import { QuestionAnswerSummary } from './QuestionAnswerSummary';
import {
  groupQuestionAnswers,
  type QuestionAnswerEntry,
} from './questionAnswerGroups';

// Bounds the client-side Gate-2 correction loop (validate_graph -> re-chat
// with correction_errors -> validate_graph again). Separate from and much
// smaller than the backend's own MAX_CORRECTION_ATTEMPTS, which already
// exhausted Gate 1 (structural) retries before ever returning a plan here.
const MAX_CLIENT_CORRECTION_ROUNDS = 2;

// v1: one image/PDF attachment per message -- mirrors the backend's own
// ALLOWED_CONTENT_TYPES/MAX_ATTACHMENT_BYTES in services/attachments.py.
// Kept in sync manually since there's no shared schema between the two.
const ATTACHMENT_ACCEPT =
  'image/png,image/jpeg,image/gif,image/webp,application/pdf';
const MAX_ATTACHMENT_BYTES = 8 * 1024 * 1024;
const PROMPT_ENTER_DURATION_MS = 560;
const ACKNOWLEDGEMENT_REVEAL_DELAY_MS = 700;
const QUESTION_ANSWER_HANDOFF_MS = 520;
// Candidate safety bounds while replay calibration is collected. The backend
// rollout gate keeps prepare_then_finalize disabled until these are funded.
const PREPARATION_STALL_MS = 8_000;
const PREPARATION_PHASE_BUDGET_MS = 60_000;
const PREPARATION_JOIN_POLL_MS = 250;
const STOP_RECONCILIATION_ERROR =
  'Copilot could not safely stop. Reload this page before editing the workflow.';

function FilledSendIcon() {
  return (
    <svg
      width='19'
      height='19'
      viewBox='0 0 24 24'
      fill='currentColor'
      aria-hidden='true'
    >
      <path d='M2.01 21 23 12 2.01 3 2 10l15 2-15 2z' />
    </svg>
  );
}

function waitForRetry(delayMs: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const timeout = window.setTimeout(resolve, delayMs);
    signal?.addEventListener(
      'abort',
      () => {
        window.clearTimeout(timeout);
        reject(new DOMException('Aborted', 'AbortError'));
      },
      { once: true }
    );
  });
}

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/** One card in a proposed plan. Its checkpoint is the graph immediately
 * after this step; the turn also owns a separate pre-first-step snapshot. */
type PlanRow = {
  id: string;
  kind: 'step';
  title: string;
  value?: string;
  valueRevealKey?: string;
  valueRevealDelayMs?: number;
  nodeId: string | undefined;
  completed: boolean;
  checkpointId: string | undefined;
};

type PlanAnsweredQuestion = {
  id: string;
  prompt: string;
  labels: string[];
  templateChunks?: TextTemplateChunk[];
  animateIn?: boolean;
};

type DepartingQuestion = {
  question: AgentStructuredQuestion;
};

type LiveTurn =
  | {
      id: string;
      role: 'user';
      content: string;
      attachment?: AgentAttachmentMeta | null;
      answerChips?: string[];
      answerTemplateChunks?: TextTemplateChunk[];
      answerQuestionPrompt?: string;
      animateIn?: boolean;
    }
  | {
      id: string;
      role: 'assistant';
      kind: 'acknowledgement' | 'clarifying_question' | 'error';
      /** Null for a structured clarification: the question card is the
       * authoritative visible copy, so model narration must stay hidden. */
      content: string | null;
      animateIn?: boolean;
      /** Acknowledgements wait outside the DOM until their entrance begins,
       * so a transparent turn cannot reserve space above Drafting. */
      visible?: boolean;
    }
  | {
      id: string;
      role: 'assistant';
      kind: 'plan_proposed';
      /** The model's own summary of what it's about to do. */
      intro: string;
      /** A normal message turn creates its acknowledgement immediately,
       * before the model has returned enough data to build these rows. */
      showIntro: boolean;
      /** Configuration answers belong to this workflow plan visually. Keeping
       * them here prevents each answer from jumping below the plan rows. */
      answeredQuestions: PlanAnsweredQuestion[];
      rows: PlanRow[];
      baseCheckpointId: string;
      /** True once every step has been ticked (a full row list is pushed up
       * front -- see runPlanTurn -- so a row count alone can't tell "still
       * building" from "done" the way a growing array would). */
      isComplete: boolean;
      /** Post-reveal work that should remain visible under the completed plan. */
      statusLabel?: string;
    }
  | {
      id: string;
      role: 'assistant';
      kind: 'streaming';
      /** Purely a placeholder turn shown as "Drafting..." for the whole
       * send -- the model's raw thinking/tool-lookup narration is never
       * surfaced to the user. Once the turn resolves, the real reply
       * renders as its own fresh turn via a different LiveTurn kind. */
    };

type SegmentRecovery = {
  outcome: AgentChatTurnResponse;
  sessionId: string;
  /** Authoritative canvas state from before this segment. Used only when
   * every exact-snapshot save attempt fails and Stop must remove unsaved
   * presentation. */
  baseNodes: ReactFlowNode[];
  baseEdges: ReactFlowEdge[];
  finalNodes: ReactFlowNode[];
  finalEdges: ReactFlowEdge[];
  refMap: Map<string, string>;
  receiptRequestId: string;
  persisted: WorkflowGraphWriteResult | null;
  validatedFingerprint?: string;
  terminalCode?: 'workflow_graph_conflict' | 'workflow_not_draft';
  compatibilityError?: boolean;
};

type DiscardCapability = {
  draftId: string;
  expiresAt?: string;
};

type DraftRecoveryStatus = {
  terminalStatus: string;
  failureCode?: string | null;
};

type SegmentPersistAttempt = {
  recovery: SegmentRecovery;
  promise: Promise<WorkflowGraphWriteResult>;
};

type SegmentHandshakeState =
  'persisting_segment' | 'acknowledging_segment' | 'receipt_error' | null;

type SegmentHandshakeResult = {
  question: AgentStructuredQuestion | null;
  acknowledgement: AgentChatTurnResponse;
};

type PreparationTask = {
  draftId: string;
  controller: AbortController;
  startedAt: number;
  lastProgressAt: number;
  promise: Promise<AgentChatTurnResponse | null>;
};

type RunPlanTurn = (
  outcome: AgentChatTurnResponse,
  token: string,
  sessionIdForCorrection: string,
  turnId: string,
  latencyTrace?: CopilotLatencyTrace,
  preserveCompletedPlan?: boolean
) => Promise<void>;

interface WorkflowAgentChatPanelProps {
  open: boolean;
  onClose: () => void;
  workflowId: string;
  workflowGraphRevision: number;
  nodes: ReactFlowNode[];
  edges: ReactFlowEdge[];
  durableDraftEnabled: boolean;
  onPersistSegment: (
    nodes: ReactFlowNode[],
    edges: ReactFlowEdge[]
  ) => Promise<WorkflowGraphWriteResult | null>;
  /** A plan passed Gate 2 with nothing to reveal (an empty ops list) --
   * apply it to the canvas directly, same as any manual edit. Every
   * non-degenerate plan goes through onStartReveal instead. Protocol-v2
   * configuration can identify existing nodes whose changed values should
   * animate in the same render that first applies them. */
  onApplyPlan: (
    nodes: ReactFlowNode[],
    edges: ReactFlowEdge[],
    presentation?: { animateNodeIds?: string[] }
  ) => void;
  /** Replace presentation with a graph already known to be authoritative.
   * This also resets the auto-persist/unload baseline without a network save.
   * Configuration may identify existing nodes whose new values should reuse
   * the canvas reveal treatment without changing their graph identity. */
  onReconcileSegmentGraph: (
    nodes: ReactFlowNode[],
    edges: ReactFlowEdge[],
    presentation?: { animateNodeIds?: string[] }
  ) => void;
  /** usePlanReveal's start(), owned by LBCanvas.tsx -- replays a validated
   * plan onto the canvas step by step instead of applying it atomically. */
  onStartReveal: (args: {
    steps: RevealStepInput[];
    onStepApplied: (stepIndex: number) => void;
    onPhaseComplete: (
      phaseIndex: number,
      nodes: ReactFlowNode[],
      edges: ReactFlowEdge[]
    ) => void;
  }) => Promise<void>;
  /** usePlanReveal's isRevealing, used to lock composer actions. */
  isRevealing: boolean;
  /** usePlanReveal's cancel(). */
  onCancelReveal: (args: { flushToFinal: boolean }) => void;
  /** handleRestoreAgentSnapshot -- restores a checkpoint by its already-
   * deserialized nodes/edges plus its stored position map. Cancels any
   * in-flight reveal itself, so callers don't need to. */
  onRestoreSnapshot: (
    nodes: ReactFlowNode[],
    edges: ReactFlowEdge[],
    positions: Map<string, { x: number; y: number }>
  ) => void;
  /** Owns the canvas-wide mutation lock for the whole Copilot lifecycle. */
  onDraftLockChange: (
    reason: 'generating' | 'revealing' | 'awaiting_answer' | null
  ) => void;
  /** Suppress only the validation presentation for nodes Copilot still owns.
   * Raw validation remains authoritative for publish and backend receipt. */
  onConfigurationLifecycleChange: (
    states: AgentConfigurationNodeState[]
  ) => void;
  onOpenNodeConfiguration: (nodeId: string) => void;
  /**
   * Sent automatically as the first user message once the session starts
   * (from the Automations empty-state's Generate/template flow). Only
   * fires once per mount -- safe because it's only ever passed for a
   * brand-new workflow with a brand-new, empty session.
   */
  initialMessage?: string;
}

export const WorkflowAgentChatPanel = forwardRef<
  HTMLDivElement,
  WorkflowAgentChatPanelProps
>(function WorkflowAgentChatPanel(
  {
    open,
    onClose,
    workflowId,
    workflowGraphRevision,
    nodes,
    edges,
    durableDraftEnabled,
    onPersistSegment,
    onApplyPlan,
    onReconcileSegmentGraph,
    onStartReveal,
    isRevealing,
    onCancelReveal,
    onRestoreSnapshot,
    onDraftLockChange,
    onConfigurationLifecycleChange,
    onOpenNodeConfiguration,
    initialMessage,
  }: WorkflowAgentChatPanelProps,
  ref
) {
  const { getToken } = useToken();
  const {
    checkpoints,
    addCheckpoint,
    getCheckpoint,
    refreshLatestTurnCheckpoints,
    clear: clearCheckpoints,
  } = useAgentCheckpoints();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessionStateVersion, setSessionStateVersion] = useState(0);
  const [activeDraftId, setActiveDraftId] = useState<string | null>(null);
  const [pendingTopologyClarificationId, setPendingTopologyClarificationId] =
    useState<string | null>(null);
  const [draftProtocolVersion, setDraftProtocolVersion] =
    useState<AgentDraftProtocolVersion>(1);
  const [discardCapability, setDiscardCapability] =
    useState<DiscardCapability | null>(null);
  const [draftRecoveryStatus, setDraftRecoveryStatus] =
    useState<DraftRecoveryStatus | null>(null);
  const [resumableConfiguration, setResumableConfiguration] = useState(false);
  const [historyTurns, setHistoryTurns] = useState<
    Array<AgentConversationTurn & { id: string }>
  >([]);
  const [liveTurns, setLiveTurns] = useState<LiveTurn[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isSubmittingAnswer, setIsSubmittingAnswer] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [retiredDraftNotice, setRetiredDraftNotice] = useState<string | null>(
    null
  );
  const [startRetryVersion, setStartRetryVersion] = useState(0);
  const [workerUpdating, setWorkerUpdating] = useState(false);
  const workerRetryRef = useRef<(() => void) | null>(null);
  const resumableConfigurationStageRef = useRef<'prepare' | 'finalize'>(
    'finalize'
  );
  const pauseConfigurationForWorkerUpdate = useCallback(
    (stage: 'prepare' | 'finalize' = 'finalize') => {
      resumableConfigurationStageRef.current = stage;
      setWorkerUpdating(true);
      workerRetryRef.current = () => {
        setWorkerUpdating(false);
        setResumableConfiguration(true);
      };
    },
    []
  );
  const authoringSchemaRef = useRef<AuthoringSchema | undefined>(undefined);
  const [recoveredAnswer, setRecoveredAnswer] = useState<{
    questionId: string;
    values: string[];
    templateChunks?: TextTemplateChunk[];
  } | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [pendingQuestion, setPendingQuestion] =
    useState<AgentStructuredQuestion | null>(null);
  const [resolvedTeamQuestion, setResolvedTeamQuestion] =
    useState<AgentStructuredQuestion | null>(null);
  const [departingQuestion, setDepartingQuestion] =
    useState<DepartingQuestion | null>(null);
  const [segmentHandshakeState, setSegmentHandshakeState] =
    useState<SegmentHandshakeState>(null);
  const [segmentRecovery, setSegmentRecovery] =
    useState<SegmentRecovery | null>(null);
  const [workflowConflict, setWorkflowConflict] = useState(false);
  const [attachedFile, setAttachedFile] = useState<{
    file: File;
    dataBase64: string;
  } | null>(null);
  const [attachError, setAttachError] = useState<string | null>(null);

  const startedForWorkflowRef = useRef<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const autoSentInitialMessageRef = useRef(false);
  const acknowledgementTurnIdsRef = useRef(new Map<string, string>());
  const acknowledgementRevealTimersRef = useRef(new Map<string, number>());
  const answerHandoffTimerRef = useRef<number | null>(null);
  // Protocol v2 intentionally keeps the topology response compact and may
  // omit a prose summary. Retain the user's request so the acknowledgement
  // can still say what Copilot is about to build without another model call.
  const draftIntentRef = useRef<string | null>(null);
  const initialPromptAnimationRef = useRef<{
    message: string;
    startedAt: number;
  } | null>(null);

  // The initial prompt is rendered optimistically, then represented by a new
  // live-turn object once the session starts. Both DOM instances use this same
  // clock so React remounts continue the entrance animation instead of
  // visibly restarting it.
  if (
    initialMessage &&
    initialPromptAnimationRef.current?.message !== initialMessage
  ) {
    initialPromptAnimationRef.current = {
      message: initialMessage,
      startedAt: performance.now(),
    };
  }
  const initialPromptAnimationDelay = initialPromptAnimationRef.current
    ? -Math.min(
        PROMPT_ENTER_DURATION_MS,
        performance.now() - initialPromptAnimationRef.current.startedAt
      )
    : 0;
  const initialPromptAnimationStyle = initialMessage
    ? { animationDelay: `${initialPromptAnimationDelay}ms` }
    : undefined;

  useEffect(
    () => () => {
      acknowledgementRevealTimersRef.current.forEach((timer) =>
        window.clearTimeout(timer)
      );
      acknowledgementRevealTimersRef.current.clear();
      if (answerHandoffTimerRef.current !== null) {
        window.clearTimeout(answerHandoffTimerRef.current);
      }
    },
    []
  );

  // Stable refs so applyPlan (invoked from a click handler potentially several
  // ticks after the graph last changed) always folds onto the live canvas
  // state, not a stale closure over it.
  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;
  const edgesRef = useRef(edges);
  edgesRef.current = edges;

  // Team questions are hydrated from the same permission-scoped company API
  // used by notification editors. The server-provided IDs remain the
  // allowlist; API data supplies current labels, colors, setup state, and the
  // exact `users.length === 0` disabled rule.
  useEffect(() => {
    setResolvedTeamQuestion(null);
    if (!pendingQuestion || pendingQuestion.kind !== 'team_picker') return;
    if (pendingQuestion.resource_kind === 'email') return;
    const token = getToken();
    if (!token) return;
    let cancelled = false;
    void GetUsersByTeamIds(token, [])
      .then((response) => {
        if (cancelled) return;
        const teams = response.data.data.data.teams_users ?? [];
        const allowed = new Map(
          pendingQuestion.choices.map((choice) => [choice.value, choice])
        );
        const provider = pendingQuestion.resource_kind;
        const choices = teams
          .filter((team) => allowed.has(String(team.id)))
          .map((team) => {
            const serverChoice = allowed.get(String(team.id));
            const connected =
              provider === 'slack'
                ? Boolean(team.slack_connected)
                : provider === 'teams'
                  ? Boolean(team.teams_webhook)
                  : serverChoice?.status !== 'setup_required';
            return {
              value: String(team.id),
              label: team.name,
              color: team.color,
              disabled: team.users.length === 0,
              status: connected ? 'connected' : 'setup_required',
              can_setup: Boolean(team.permissions?.is_update),
            };
          });
        setResolvedTeamQuestion({ ...pendingQuestion, choices });
      })
      .catch(() => {
        // The persisted server choices are already company- and
        // permission-scoped, so a transient refresh failure remains usable.
      });
    return () => {
      cancelled = true;
    };
  }, [pendingQuestion, getToken]);

  const displayedQuestion = resolvedTeamQuestion ?? pendingQuestion;

  const currentSerializedData = useCallback(
    (n: ReactFlowNode[], e: ReactFlowEdge[]) =>
      lbWorkflowToBase64(reactFlowToLBWorkflow(n, e)),
    []
  );

  // Aborts an in-flight streamAgentMessage fetch on unmount. The panel
  // itself only ever hides via CSS (see the `!open` early return below,
  // which skips rendering but not unmounting) -- this guards the real
  // unmount case (e.g. the workflow is published, which stops rendering
  // this component entirely).
  const streamAbortRef = useRef<AbortController | null>(null);
  const preparationTaskRef = useRef<PreparationTask | null>(null);
  const configurationExecutionModeRef = useRef<
    'serial' | 'prepare_then_finalize'
  >('serial');
  const requestEpochRef = useRef(0);
  const revealGenerationRef = useRef(0);
  // The exact save is intentionally allowed to outlive presentation and an
  // aborted model/receipt request. Stop waits on this attempt before
  // unlocking so it can preserve committed work or remove only unsaved UI.
  const segmentPersistAttemptRef = useRef<SegmentPersistAttempt | null>(null);
  const sessionStateVersionRef = useRef(sessionStateVersion);
  sessionStateVersionRef.current = sessionStateVersion;
  const graphRevisionRef = useRef(workflowGraphRevision);
  useEffect(() => {
    graphRevisionRef.current = workflowGraphRevision;
  }, [workflowGraphRevision]);
  const activeDraftIdRef = useRef(activeDraftId);
  activeDraftIdRef.current = activeDraftId;
  const stopDraftingRef = useRef<Promise<void> | null>(null);
  // ReactFlow can publish multiple node-array updates for one restore
  // (structural state first, then measurements/selection). Track semantic
  // workflow bytes rather than consuming a one-render boolean so those
  // presentation-only updates cannot erase the redo suffix.
  const restoringCheckpointBytesRef = useRef<string | null>(null);
  const lastObservedGraphBytesRef = useRef<string | null>(null);
  const runPlanTurnRef = useRef<RunPlanTurn | null>(null);
  useEffect(() => {
    return () => {
      streamAbortRef.current?.abort();
      preparationTaskRef.current?.controller.abort();
      preparationTaskRef.current = null;
      requestEpochRef.current += 1;
      revealGenerationRef.current += 1;
      onDraftLockChange(null);
      onConfigurationLifecycleChange([]);
    };
  }, [onDraftLockChange, onConfigurationLifecycleChange]);

  useEffect(() => {
    // Hiding the still-mounted panel (for example by switching editor tabs)
    // must not silently release ownership while a draft can still save or
    // mutate the graph. The header X uses the explicit Stop-equivalent path;
    // passive visibility changes retain the lock until work settles.
    if (workflowConflict || pendingQuestion || activeDraftId) {
      onDraftLockChange('awaiting_answer');
    } else if (isSending) {
      onDraftLockChange('generating');
    } else if (isRevealing) {
      onDraftLockChange('revealing');
    } else {
      onDraftLockChange(null);
    }
  }, [
    open,
    pendingQuestion,
    activeDraftId,
    workflowConflict,
    isSending,
    isRevealing,
    onDraftLockChange,
  ]);

  const reconcileAndStopDrafting = useCallback((): Promise<void> => {
    if (stopDraftingRef.current) return stopDraftingRef.current;

    const stopWork = (async () => {
      setWorkerUpdating(false);
      workerRetryRef.current = null;
      setRecoveredAnswer(null);
      setResumableConfiguration(false);
      streamAbortRef.current?.abort();
      preparationTaskRef.current?.controller.abort();
      preparationTaskRef.current = null;
      requestEpochRef.current += 1;
      revealGenerationRef.current += 1;

      const persistAttempt = segmentPersistAttemptRef.current;
      if (isRevealing) {
        // If an exact save is in flight, keep the current presentation until
        // its outcome is known. We then show either the committed final graph
        // or the pre-segment graph; no unsaved final snapshot is unlocked.
        onCancelReveal({ flushToFinal: persistAttempt === null });
      }

      let authoritativeNodes = nodesRef.current;
      let authoritativeEdges = edgesRef.current;
      if (persistAttempt) {
        try {
          await persistAttempt.promise;
          authoritativeNodes = persistAttempt.recovery.finalNodes;
          authoritativeEdges = persistAttempt.recovery.finalEdges;
        } catch {
          authoritativeNodes = persistAttempt.recovery.baseNodes;
          authoritativeEdges = persistAttempt.recovery.baseEdges;
        }
        onReconcileSegmentGraph(authoritativeNodes, authoritativeEdges);
      }

      const token = getToken();
      const draftId = activeDraftIdRef.current;
      if (token && sessionId && draftId) {
        const controller = new AbortController();
        window.setTimeout(() => controller.abort(), 3000);
        void cancelAgentDraft(
          token,
          {
            sessionId,
            workflowId,
            serializedData: currentSerializedData(
              authoritativeNodes,
              authoritativeEdges
            ),
            draftId,
            clientRequestId: uuidv4(),
            draftProtocolVersion,
          },
          controller.signal
        )
          .then((outcome) => {
            if (outcome.session_state_version !== undefined) {
              sessionStateVersionRef.current = outcome.session_state_version;
              setSessionStateVersion(outcome.session_state_version);
            }
          })
          .catch(() => {
            // Cancellation is best effort. The exact save outcome has already
            // been reconciled, so unlocking cannot expose provisional data.
          });
      }

      if (segmentPersistAttemptRef.current === persistAttempt) {
        segmentPersistAttemptRef.current = null;
      }
      setIsSending(false);
      setIsSubmittingAnswer(false);
      streamAbortRef.current = null;
      setActiveDraftId(null);
      setPendingTopologyClarificationId(null);
      setPendingQuestion(null);
      setDepartingQuestion(null);
      if (answerHandoffTimerRef.current !== null) {
        window.clearTimeout(answerHandoffTimerRef.current);
        answerHandoffTimerRef.current = null;
      }
      setSegmentHandshakeState(null);
      setSegmentRecovery(null);
      setWorkflowConflict(false);
      setAttachError((error) =>
        error === STOP_RECONCILIATION_ERROR ? null : error
      );
      onConfigurationLifecycleChange([]);
      onDraftLockChange(null);
    })();
    const trackedStop = stopWork.finally(() => {
      stopDraftingRef.current = null;
    });
    stopDraftingRef.current = trackedStop;
    return trackedStop;
  }, [
    isRevealing,
    onCancelReveal,
    onReconcileSegmentGraph,
    getToken,
    sessionId,
    workflowId,
    currentSerializedData,
    onDraftLockChange,
    onConfigurationLifecycleChange,
    draftProtocolVersion,
  ]);

  const handleStopFailure = useCallback(() => {
    // Do not hide the panel or unlock a graph whose reconciliation failed.
    setAttachError(STOP_RECONCILIATION_ERROR);
  }, []);

  const handleClosePanel = useCallback(() => {
    reconcileAndStopDrafting().then(onClose).catch(handleStopFailure);
  }, [reconcileAndStopDrafting, onClose, handleStopFailure]);

  // Clear the old workflow before the start effect below launches the new
  // session request. Keeping this effect first also ensures the old request
  // is aborted and cannot repopulate the panel after navigation.
  useEffect(() => {
    if (
      startedForWorkflowRef.current !== null &&
      startedForWorkflowRef.current !== workflowId
    ) {
      streamAbortRef.current?.abort();
      preparationTaskRef.current?.controller.abort();
      preparationTaskRef.current = null;
      requestEpochRef.current += 1;
      revealGenerationRef.current += 1;
      if (isRevealing) onCancelReveal({ flushToFinal: false });
      // The shared auto-persist chain still awaits this old-workflow PUT
      // before accepting a new one. Detach its presentation metadata so a
      // later Stop on the newly loaded workflow can never restore old nodes.
      segmentPersistAttemptRef.current = null;
      setIsStarting(false);
      setIsSending(false);
      setIsSubmittingAnswer(false);
      setStartError(null);
      setSessionId(null);
      setHistoryTurns([]);
      setLiveTurns([]);
      setPendingQuestion(null);
      setDepartingQuestion(null);
      if (answerHandoffTimerRef.current !== null) {
        window.clearTimeout(answerHandoffTimerRef.current);
        answerHandoffTimerRef.current = null;
      }
      setActiveDraftId(null);
      setSegmentHandshakeState(null);
      setSegmentRecovery(null);
      setWorkflowConflict(false);
      setDraftProtocolVersion(1);
      configurationExecutionModeRef.current = 'serial';
      setDiscardCapability(null);
      setDraftRecoveryStatus(null);
      setResumableConfiguration(false);
      draftIntentRef.current = null;
      acknowledgementTurnIdsRef.current.clear();
      acknowledgementRevealTimersRef.current.forEach((timer) =>
        window.clearTimeout(timer)
      );
      acknowledgementRevealTimersRef.current.clear();
      onConfigurationLifecycleChange([]);
      clearCheckpoints();
    }
  }, [
    workflowId,
    clearCheckpoints,
    isRevealing,
    onCancelReveal,
    onConfigurationLifecycleChange,
  ]);

  useEffect(() => {
    if (!open || !workflowId) return;
    if (startedForWorkflowRef.current === workflowId) return;
    startedForWorkflowRef.current = workflowId;

    const token = getToken();
    if (!token) {
      setStartError('Not authenticated.');
      return;
    }

    setIsStarting(true);
    setWorkerUpdating(false);
    setStartError(null);
    setRetiredDraftNotice(null);
    const controller = new AbortController();
    streamAbortRef.current = controller;
    const epoch = ++requestEpochRef.current;
    const supportsProtocolV2 = durableDraftEnabled;
    startAgentSession(
      token,
      workflowId,
      supportsProtocolV2 ? [2, 1] : [1],
      controller.signal
    )
      .then((res) => {
        if (epoch !== requestEpochRef.current) return;
        const activeDraft = res.active_draft;
        const isTerminal = Boolean(activeDraft?.terminal_status);
        const isRetired =
          isTerminal || activeDraft?.draft_status === 'complete';
        readGraphOpsVersion(
          isRetired
            ? res.graph_ops_version
            : (activeDraft?.graph_ops_version ?? res.graph_ops_version)
        );
        const retiredLegacyDraft =
          activeDraft?.terminal_status === 'incompatible' &&
          activeDraft.graph_ops_version !== 2;
        if (retiredLegacyDraft) {
          // Retired state is history, not executable GraphOps. Its old pin
          // must not prevent this browser from starting a format-2 request.
          setRetiredDraftNotice(
            'This older Copilot draft cannot be resumed. Start a new request; your saved workflow is unchanged.'
          );
        }
        authoringSchemaRef.current = bindAuthoringSchema(
          res.authoring_schema,
          res.catalog_hash
        );
        setSessionId(res.session_id);
        setSessionStateVersion(res.session_state_version ?? 0);
        // The history contract has no turn ID. Assign one at ingestion, not
        // during render; repeated messages remain distinct and stable.
        setHistoryTurns(
          res.conversation_history.map((turn) => ({ ...turn, id: uuidv4() }))
        );
        const selectedProtocol: AgentDraftProtocolVersion =
          activeDraft?.draft_protocol_version === 2 ||
          (supportsProtocolV2 &&
            res.supported_draft_protocol_versions?.includes(2))
            ? 2
            : 1;
        setDraftProtocolVersion(selectedProtocol);
        configurationExecutionModeRef.current =
          activeDraft?.configuration_execution_mode ?? 'serial';
        const topologyClarification = !isRetired
          ? activeDraft?.topology_clarification
          : undefined;
        setPendingTopologyClarificationId(topologyClarification?.id ?? null);
        setActiveDraftId(
          activeDraft &&
            activeDraft.draft_status !== 'complete' &&
            !isTerminal &&
            !topologyClarification
            ? activeDraft.draft_id
            : null
        );
        setDraftRecoveryStatus(
          activeDraft?.terminal_status && !retiredLegacyDraft
            ? {
                terminalStatus: activeDraft.terminal_status,
                failureCode: activeDraft.bounded_failure_code,
              }
            : null
        );
        setDiscardCapability(
          activeDraft?.discard_available
            ? {
                draftId: activeDraft.draft_id,
                expiresAt: activeDraft.discard_expires_at,
              }
            : null
        );
        onConfigurationLifecycleChange(
          !isRetired ? (activeDraft?.configuration_node_states ?? []) : []
        );
        setResumableConfiguration(
          activeDraft?.draft_protocol_version === 2 &&
            !isTerminal &&
            activeDraft.draft_status === 'configuring'
        );
        if (activeDraft && activeDraft.workflow_graph_revision !== undefined) {
          graphRevisionRef.current = activeDraft.workflow_graph_revision;
        }
        const lastTurn = res.conversation_history.at(-1);
        setPendingQuestion(
          isRetired
            ? null
            : (activeDraft?.question ??
                (lastTurn?.v2_outcome?.draft_status === 'awaiting_answer' &&
                lastTurn.v2_outcome.draft_protocol_version !== 1
                  ? (lastTurn.v2_outcome.question ?? null)
                  : null))
        );
      })
      .catch((error) => {
        if (epoch !== requestEpochRef.current) return;
        if (
          error instanceof AgentApiError &&
          error.status === 409 &&
          error.code === 'worker_catalog_incompatible'
        ) {
          setWorkerUpdating(true);
          workerRetryRef.current = () => {
            startedForWorkflowRef.current = null;
            setStartRetryVersion((version) => version + 1);
          };
        }
        setStartError(
          error instanceof Error
            ? error.message
            : 'Failed to start Copilot session.'
        );
      })
      .finally(() => {
        if (epoch === requestEpochRef.current) {
          setIsStarting(false);
          streamAbortRef.current = null;
        }
      });
  }, [
    open,
    workflowId,
    getToken,
    durableDraftEnabled,
    onConfigurationLifecycleChange,
    startRetryVersion,
  ]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ block: 'end' });
  }, [liveTurns, isSending]);

  // Replaces a single turn in place by id. Every exit path of runPlanTurn
  // (and the streaming segment updates before it) goes through this instead
  // of a positional "last element" swap -- a turn whose rows update
  // repeatedly (checkpoints landing over the course of a multi-second
  // reveal, in a later milestone) can't rely on always being the last
  // element of the array by the time an update arrives.
  const updateTurn = useCallback(
    (turnId: string, updater: (turn: LiveTurn) => LiveTurn) => {
      setLiveTurns((prev) =>
        prev.map((t) => (t.id === turnId ? updater(t) : t))
      );
    },
    []
  );

  const settleAcknowledgement = useCallback(
    (turnId: string, content: string | null) => {
      const acknowledgementId = acknowledgementTurnIdsRef.current.get(turnId);
      if (!acknowledgementId) return;
      const timer =
        acknowledgementRevealTimersRef.current.get(acknowledgementId);
      if (timer !== undefined) window.clearTimeout(timer);
      acknowledgementRevealTimersRef.current.delete(acknowledgementId);
      setLiveTurns((previous) =>
        content === null
          ? previous.filter((turn) => turn.id !== acknowledgementId)
          : previous.map((turn) =>
              turn.id === acknowledgementId &&
              turn.role === 'assistant' &&
              turn.kind === 'acknowledgement'
                ? {
                    ...turn,
                    content,
                    // Once topology is ready, keep the acknowledgement as a
                    // settled transcript row. Leaving the entrance class on
                    // this node lets the checklist transition replay its
                    // opacity/height animation, which looks like the response
                    // disappeared while widgets mount.
                    visible: true,
                    animateIn: false,
                  }
                : turn
            )
      );
      if (content === null) acknowledgementTurnIdsRef.current.delete(turnId);
    },
    []
  );

  const showTurnError = useCallback((turnId: string, content: string) => {
    setLiveTurns((previous) => {
      const existing = previous.find((turn) => turn.id === turnId);
      const errorTurn: LiveTurn = {
        id: uuidv4(),
        role: 'assistant',
        kind: 'error',
        content,
      };
      if (
        existing?.role === 'assistant' &&
        existing.kind === 'plan_proposed' &&
        existing.isComplete
      ) {
        return [...previous, errorTurn];
      }
      return previous.map((turn) =>
        turn.id === turnId ? { ...errorTurn, id: turnId } : turn
      );
    });
  }, []);

  const invalidateRedoSuffixes = useCallback(() => {
    setLiveTurns((previous) =>
      previous.map((turn) => {
        if (
          turn.role !== 'assistant' ||
          turn.kind !== 'plan_proposed' ||
          !turn.rows.some((row) => !row.completed && row.checkpointId)
        ) {
          return turn;
        }
        return {
          ...turn,
          rows: turn.rows.map((row) =>
            row.completed ? row : { ...row, checkpointId: undefined }
          ),
        };
      })
    );
  }, []);

  const persistSegmentSnapshot = useCallback(
    (
      recovery: SegmentRecovery,
      attempt: number,
      latencyTrace?: CopilotLatencyTrace
    ): Promise<WorkflowGraphWriteResult> => {
      setSegmentHandshakeState('persisting_segment');
      const endPersist = latencyTrace?.startPhase('final_segment_persist', {
        attempt,
      });
      const persistPromise = (async () => {
        try {
          const persisted = await onPersistSegment(
            recovery.finalNodes,
            recovery.finalEdges
          );
          if (!persisted) {
            throw new Error('The drafted graph could not be persisted.');
          }
          graphRevisionRef.current = persisted.graph_revision;
          latencyTrace?.mark('segment_save_response', { attempt });
          return persisted;
        } finally {
          endPersist?.();
        }
      })();
      const persistAttempt: SegmentPersistAttempt = {
        recovery,
        promise: persistPromise,
      };
      segmentPersistAttemptRef.current = persistAttempt;
      return persistPromise;
    },
    [onPersistSegment]
  );

  const performSegmentHandshake = useCallback(
    async (
      recovery: SegmentRecovery,
      token: string,
      signal?: AbortSignal,
      allowAutomaticRetry = true,
      latencyTrace?: CopilotLatencyTrace,
      initialPersistPromise?: Promise<WorkflowGraphWriteResult>
    ): Promise<SegmentHandshakeResult | null> => {
      let currentRecovery = recovery;
      let pendingPersistPromise = initialPersistPromise;
      const attempts = allowAutomaticRetry ? 2 : 1;
      for (let attempt = 0; attempt < attempts; attempt++) {
        try {
          if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
          let persisted = currentRecovery.persisted;
          if (!persisted) {
            const persistPromise =
              pendingPersistPromise ??
              persistSegmentSnapshot(
                currentRecovery,
                attempt + 1,
                latencyTrace
              );
            pendingPersistPromise = undefined;
            persisted = await persistPromise;
            currentRecovery = { ...currentRecovery, persisted };
          }
          if (
            currentRecovery.outcome.draft_protocol_version === 2 &&
            currentRecovery.validatedFingerprint &&
            persisted.graph_fingerprint !== currentRecovery.validatedFingerprint
          ) {
            throw new AgentApiError(
              'The saved graph no longer matches the validated Copilot candidate.',
              'candidate_fingerprint_mismatch'
            );
          }
          if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
          setSegmentHandshakeState('acknowledging_segment');
          const outcome = currentRecovery.outcome;
          const endReceipt = latencyTrace?.startPhase('segment_receipt', {
            attempt: attempt + 1,
          });
          let acknowledgement: AgentChatTurnResponse;
          try {
            latencyTrace?.mark('segment_receipt_dispatched', {
              attempt: attempt + 1,
            });
            acknowledgement = await sendAgentSegmentReceipt(
              token,
              {
                sessionId: currentRecovery.sessionId,
                workflowId,
                serializedData: currentSerializedData(
                  currentRecovery.finalNodes,
                  currentRecovery.finalEdges
                ),
                draftId: outcome.draft_id!,
                segmentId: outcome.segment_id!,
                workflowGraphRevision: persisted.graph_revision,
                graphFingerprint: persisted.graph_fingerprint,
                refMap: Object.fromEntries(currentRecovery.refMap),
                expectedSessionStateVersion:
                  outcome.session_state_version ??
                  sessionStateVersionRef.current,
                clientRequestId: currentRecovery.receiptRequestId,
                draftProtocolVersion: outcome.draft_protocol_version,
                candidateAttemptId: outcome.candidate_attempt_id,
                phaseClaimVersion: outcome.phase_claim_version,
              },
              signal
            );
            latencyTrace?.mark('segment_receipt_response', {
              attempt: attempt + 1,
            });
          } finally {
            endReceipt?.();
          }
          if (acknowledgement.session_state_version !== undefined) {
            sessionStateVersionRef.current =
              acknowledgement.session_state_version;
            setSessionStateVersion(acknowledgement.session_state_version);
          }
          const remainsActive =
            acknowledgement.draft_protocol_version === 2
              ? acknowledgement.draft_status !== 'complete'
              : acknowledgement.draft_status === 'awaiting_answer';
          setActiveDraftId(remainsActive ? outcome.draft_id! : null);
          onConfigurationLifecycleChange(
            acknowledgement.configuration_node_states ?? []
          );
          setDiscardCapability(
            remainsActive && acknowledgement.discard_available
              ? {
                  draftId: outcome.draft_id!,
                  expiresAt: acknowledgement.discard_expires_at,
                }
              : null
          );
          setSegmentHandshakeState(null);
          setSegmentRecovery(null);
          if (
            segmentPersistAttemptRef.current?.recovery.outcome.segment_id ===
            outcome.segment_id
          ) {
            segmentPersistAttemptRef.current = null;
          }
          return {
            question: remainsActive
              ? (acknowledgement.question ?? outcome.question ?? null)
              : null,
            acknowledgement,
          };
        } catch (error) {
          if (signal?.aborted) throw error;
          if (
            error instanceof AgentApiError &&
            error.status === 409 &&
            error.code === 'worker_catalog_incompatible'
          ) {
            setSegmentHandshakeState('receipt_error');
            setSegmentRecovery({
              ...currentRecovery,
              compatibilityError: true,
            });
            return null;
          }
          if (
            currentRecovery.outcome.draft_protocol_version === 2 &&
            error instanceof AgentApiError &&
            [
              'agent_capability_changed',
              'agent_descriptor_changed',
              'candidate_validation_failed',
              'configuration_question_invalid',
            ].includes(error.code ?? '')
          ) {
            setActiveDraftId(null);
            setSegmentHandshakeState(null);
            setSegmentRecovery(null);
            setDraftRecoveryStatus({
              terminalStatus: 'failed_manual_recovery',
              failureCode: error.code,
            });
            onConfigurationLifecycleChange([]);
            try {
              const sessions = await listAgentSessions(
                token,
                workflowId,
                signal
              );
              const status = sessions.find(
                (item) => item.session_id === currentRecovery.sessionId
              )?.draft_status;
              if (status?.discard_available) {
                setDiscardCapability({
                  draftId: currentRecovery.outcome.draft_id!,
                  expiresAt: status.discard_expires_at,
                });
              }
            } catch {
              // The stable manual-recovery state is still actionable without
              // the optional, time-bounded Discard capability.
            }
            return null;
          }
          if (
            (error instanceof AgentApiError ||
              error instanceof WorkflowGraphApiError) &&
            ['workflow_graph_conflict', 'workflow_not_draft'].includes(
              error.code ?? ''
            )
          ) {
            setSegmentHandshakeState('receipt_error');
            setSegmentRecovery({
              ...currentRecovery,
              terminalCode: error.code as SegmentRecovery['terminalCode'],
            });
            return null;
          }
          if (attempt + 1 < attempts) {
            await new Promise((resolve) => window.setTimeout(resolve, 500));
            continue;
          }
          setSegmentHandshakeState('receipt_error');
          setSegmentRecovery(currentRecovery);
          return null;
        }
      }
      return null;
    },
    [
      currentSerializedData,
      persistSegmentSnapshot,
      workflowId,
      onConfigurationLifecycleChange,
    ]
  );

  const acknowledgeSegment = useCallback(
    async (
      outcome: AgentChatTurnResponse,
      token: string,
      sessionIdForReceipt: string,
      finalNodes: ReactFlowNode[],
      finalEdges: ReactFlowEdge[],
      refMap: Map<string, string>,
      latencyTrace?: CopilotLatencyTrace
    ): Promise<AgentStructuredQuestion | null> => {
      if (!outcome.draft_id || !outcome.segment_id) {
        return outcome.draft_status === 'awaiting_answer'
          ? (outcome.question ?? null)
          : null;
      }
      const result = await performSegmentHandshake(
        {
          outcome,
          sessionId: sessionIdForReceipt,
          baseNodes: nodesRef.current,
          baseEdges: edgesRef.current,
          finalNodes,
          finalEdges,
          refMap,
          receiptRequestId: uuidv4(),
          persisted: null,
        },
        token,
        streamAbortRef.current?.signal,
        true,
        latencyTrace
      );
      return result?.question ?? null;
    },
    [performSegmentHandshake]
  );

  const handleRetrySegment = useCallback(async () => {
    if (!segmentRecovery || isSending) return;
    const token = getToken();
    if (!token) return;
    setIsSending(true);
    const controller = new AbortController();
    streamAbortRef.current = controller;
    const epoch = ++requestEpochRef.current;
    try {
      const result = await performSegmentHandshake(
        segmentRecovery,
        token,
        controller.signal,
        false
      );
      if (epoch === requestEpochRef.current) {
        setPendingQuestion(result?.question ?? null);
      }
    } catch {
      // performSegmentHandshake retains the recovery card and lock.
    } finally {
      if (epoch === requestEpochRef.current) {
        setIsSending(false);
        streamAbortRef.current = null;
      }
    }
  }, [segmentRecovery, isSending, getToken, performSegmentHandshake]);

  const handleStopDrafting = useCallback(() => {
    reconcileAndStopDrafting().catch(handleStopFailure);
  }, [reconcileAndStopDrafting, handleStopFailure]);

  const handleReloadWorkflow = useCallback(() => {
    reconcileAndStopDrafting()
      .then(() => window.location.reload())
      .catch(handleStopFailure);
  }, [reconcileAndStopDrafting, handleStopFailure]);

  const handleDiscardDraft = useCallback(async () => {
    if (!discardCapability || !sessionId || isSending) return;
    if (
      !window.confirm(
        'Discard the Copilot draft and restore this workflow to its empty starting state?'
      )
    ) {
      return;
    }
    const token = getToken();
    if (!token) return;
    setIsSending(true);
    const controller = new AbortController();
    streamAbortRef.current = controller;
    const epoch = ++requestEpochRef.current;
    try {
      const result = await cancelAgentDraft(
        token,
        {
          sessionId,
          workflowId,
          serializedData: currentSerializedData(
            nodesRef.current,
            edgesRef.current
          ),
          draftId: discardCapability.draftId,
          clientRequestId: uuidv4(),
          draftProtocolVersion: 2,
          discard: true,
        },
        controller.signal
      );
      if (epoch !== requestEpochRef.current) return;
      if (result.workflow_graph_revision !== undefined) {
        graphRevisionRef.current = result.workflow_graph_revision;
      }
      onCancelReveal({ flushToFinal: false });
      onReconcileSegmentGraph([], []);
      nodesRef.current = [];
      edgesRef.current = [];
      setActiveDraftId(null);
      setPendingQuestion(null);
      setDiscardCapability(null);
      setDraftRecoveryStatus(null);
      setSegmentRecovery(null);
      setSegmentHandshakeState(null);
      onConfigurationLifecycleChange([]);
      onDraftLockChange(null);
    } catch (error) {
      if (epoch !== requestEpochRef.current) return;
      setDraftRecoveryStatus({
        terminalStatus: 'discard_failed',
        failureCode:
          error instanceof AgentApiError ? (error.code ?? null) : null,
      });
    } finally {
      if (epoch === requestEpochRef.current) {
        setIsSending(false);
        streamAbortRef.current = null;
      }
    }
  }, [
    discardCapability,
    sessionId,
    isSending,
    getToken,
    workflowId,
    currentSerializedData,
    onCancelReveal,
    onReconcileSegmentGraph,
    onConfigurationLifecycleChange,
    onDraftLockChange,
  ]);

  const streamDraftContinuation = useCallback(
    async (
      token: string,
      sessionIdForContinuation: string,
      continuationNodes: ReactFlowNode[],
      continuationEdges: ReactFlowEdge[],
      signal?: AbortSignal,
      latencyTrace?: CopilotLatencyTrace,
      configurationStage: 'prepare' | 'finalize' = 'finalize',
      onProviderProgress?: () => void
    ): Promise<AgentChatTurnResponse> => {
      const endContinuation = latencyTrace?.startPhase(
        'configuration_backend_stream'
      );
      let sawFirstEvent = false;
      try {
        return await streamAgentMessage(
          token,
          {
            sessionId: sessionIdForContinuation,
            workflowId,
            serializedData: currentSerializedData(
              continuationNodes,
              continuationEdges
            ),
            clientRequestId: uuidv4(),
            draftProtocolVersion: 2,
            workflowGraphRevision: graphRevisionRef.current,
            continueDraft: true,
            configurationStage,
          },
          (event) => {
            if (event.type === 'provider_progress') {
              onProviderProgress?.();
              return;
            }
            if (sawFirstEvent) return;
            sawFirstEvent = true;
            latencyTrace?.mark('configuration_first_sse_event', {
              stream_event_type: event.type,
              tool_name:
                event.type === 'tool_call' || event.type === 'tool_use_started'
                  ? event.name
                  : undefined,
            });
          },
          signal
        );
      } finally {
        endContinuation?.();
      }
    },
    [workflowId, currentSerializedData]
  );

  const startDraftPreparation = useCallback(
    (
      token: string,
      sessionIdForPreparation: string,
      draftId: string,
      continuationNodes: ReactFlowNode[],
      continuationEdges: ReactFlowEdge[]
    ): Promise<AgentChatTurnResponse | null> => {
      if (configurationExecutionModeRef.current !== 'prepare_then_finalize') {
        return Promise.resolve(null);
      }
      const current = preparationTaskRef.current;
      if (current?.draftId === draftId) return current.promise;
      current?.controller.abort();

      const controller = new AbortController();
      const startedAt = performance.now();
      const task: PreparationTask = {
        draftId,
        controller,
        startedAt,
        lastProgressAt: startedAt,
        promise: Promise.resolve(null),
      };
      task.promise = streamDraftContinuation(
        token,
        sessionIdForPreparation,
        continuationNodes,
        continuationEdges,
        controller.signal,
        undefined,
        'prepare',
        () => {
          task.lastProgressAt = performance.now();
        }
      )
        .then((outcome) => {
          if (outcome.configuration_execution_mode) {
            configurationExecutionModeRef.current =
              outcome.configuration_execution_mode;
          }
          return outcome.kind === 'configuration_prepared' ? outcome : null;
        })
        .catch((_error): AgentChatTurnResponse | null => null)
        .finally(() => {
          // Keep a completed task available to the final answer path. It is
          // replaced only by a new draft or explicitly cleared on Stop.
          task.lastProgressAt = performance.now();
        });
      preparationTaskRef.current = task;
      return task.promise;
    },
    [streamDraftContinuation]
  );

  const continueAfterTopologyReceipt = useCallback(
    async (
      handshake: SegmentHandshakeResult,
      token: string,
      sessionIdForContinuation: string,
      continuationNodes: ReactFlowNode[],
      continuationEdges: ReactFlowEdge[],
      signal?: AbortSignal,
      latencyTrace?: CopilotLatencyTrace
    ): Promise<AgentChatTurnResponse | null> => {
      const acknowledgement = handshake.acknowledgement;
      configurationExecutionModeRef.current =
        acknowledgement.configuration_execution_mode ?? 'serial';

      if (
        handshake.question &&
        acknowledgement.configuration_execution_mode ===
          'prepare_then_finalize' &&
        acknowledgement.draft_id
      ) {
        // Backward compatibility for an acknowledged prepared-mode draft that
        // already exposed a question. New receipts withhold questions until
        // preparation has rebuilt the complete ordered queue.
        const _ = startDraftPreparation(
          token,
          sessionIdForContinuation,
          acknowledgement.draft_id,
          continuationNodes,
          continuationEdges
        );
        return null;
      }

      if (
        !acknowledgement.continue_draft &&
        acknowledgement.draft_status !== 'configuring'
      ) {
        return null;
      }
      const stage = acknowledgement.next_configuration_stage ?? 'finalize';
      let resumeStage = stage;
      try {
        let outcome = await streamDraftContinuation(
          token,
          sessionIdForContinuation,
          continuationNodes,
          continuationEdges,
          signal,
          latencyTrace,
          stage
        );
        if (
          stage === 'prepare' &&
          outcome.kind === 'configuration_prepared' &&
          !outcome.question
        ) {
          resumeStage = 'finalize';
          outcome = await streamDraftContinuation(
            token,
            sessionIdForContinuation,
            continuationNodes,
            continuationEdges,
            signal,
            latencyTrace,
            'finalize'
          );
        }
        return outcome;
      } catch (error) {
        if (
          error instanceof AgentApiError &&
          error.status === 409 &&
          error.code === 'worker_catalog_incompatible'
        ) {
          pauseConfigurationForWorkerUpdate(resumeStage);
          return null;
        }
        throw error;
      }
    },
    [
      startDraftPreparation,
      streamDraftContinuation,
      pauseConfigurationForWorkerUpdate,
    ]
  );

  const joinLivePreparation = useCallback(
    async (
      draftId: string,
      signal?: AbortSignal
    ): Promise<AgentChatTurnResponse | null> => {
      const task = preparationTaskRef.current;
      if (!task || task.draftId !== draftId) return null;
      while (true) {
        const settled = await Promise.race([
          task.promise.then((outcome) => ({ done: true as const, outcome })),
          waitForRetry(PREPARATION_JOIN_POLL_MS, signal).then(() => ({
            done: false as const,
            outcome: null as AgentChatTurnResponse | null,
          })),
        ]);
        if (settled.done) return settled.outcome;
        const now = performance.now();
        if (
          now - task.startedAt >= PREPARATION_PHASE_BUDGET_MS ||
          now - task.lastProgressAt >= PREPARATION_STALL_MS
        ) {
          task.controller.abort();
          if (preparationTaskRef.current === task) {
            preparationTaskRef.current = null;
          }
          return null;
        }
      }
    },
    []
  );

  const refreshV2RecoveryStatus = useCallback(
    async (
      token: string,
      sessionIdForStatus: string,
      signal?: AbortSignal
    ): Promise<boolean> => {
      try {
        const sessions = await listAgentSessions(token, workflowId, signal);
        const status = sessions.find(
          (item) => item.session_id === sessionIdForStatus
        )?.draft_status;
        if (!status) return false;
        onConfigurationLifecycleChange(status.configuration_node_states ?? []);
        if (!status.terminal_status) return false;
        setActiveDraftId(null);
        setDraftRecoveryStatus({
          terminalStatus: status.terminal_status,
          failureCode: status.bounded_failure_code,
        });
        setDiscardCapability(
          status.discard_available
            ? {
                draftId: status.draft_id,
                expiresAt: status.discard_expires_at,
              }
            : null
        );
        onConfigurationLifecycleChange([]);
        return true;
      } catch {
        // Status refresh is best-effort. The original phase error remains in
        // the chat, and explicit Stop is still available for a live draft.
        return false;
      }
    },
    [workflowId, onConfigurationLifecycleChange]
  );

  const cancelRejectedV2Candidate = useCallback(
    async (
      token: string,
      sessionIdForCancellation: string,
      outcome: AgentChatTurnResponse,
      signal?: AbortSignal
    ) => {
      if (!outcome.draft_id) {
        throw new Error('The rejected Copilot draft has no durable identity.');
      }
      const cancellation = await cancelAgentDraft(
        token,
        {
          sessionId: sessionIdForCancellation,
          workflowId,
          serializedData: currentSerializedData(
            nodesRef.current,
            edgesRef.current
          ),
          draftId: outcome.draft_id,
          clientRequestId: uuidv4(),
          draftProtocolVersion: 2,
        },
        signal
      );
      if (cancellation.session_state_version !== undefined) {
        sessionStateVersionRef.current = cancellation.session_state_version;
        setSessionStateVersion(cancellation.session_state_version);
      }
      activeDraftIdRef.current = null;
      setActiveDraftId(null);
      setPendingQuestion(null);
      setDiscardCapability(null);
      setDraftRecoveryStatus(null);
      setSegmentRecovery(null);
      setSegmentHandshakeState(null);
      onConfigurationLifecycleChange([]);
      onDraftLockChange(null);
    },
    [
      workflowId,
      currentSerializedData,
      onConfigurationLifecycleChange,
      onDraftLockChange,
    ]
  );

  // A checkpoint restore changes the graph too, but it is the operation that
  // creates the redo suffix. Only a subsequent *semantic* graph change
  // abandons that suffix. ReactFlow also replaces node arrays for selection,
  // measurements, and layout bookkeeping; serialized workflow equality keeps
  // those presentation-only updates from disabling a valid redo.
  useEffect(() => {
    const graphBytes = currentSerializedData(nodes, edges);
    const previousGraphBytes = lastObservedGraphBytesRef.current;
    lastObservedGraphBytesRef.current = graphBytes;

    if (restoringCheckpointBytesRef.current === graphBytes) {
      restoringCheckpointBytesRef.current = null;
      return;
    }
    restoringCheckpointBytesRef.current = null;
    if (
      previousGraphBytes !== null &&
      graphBytes !== previousGraphBytes &&
      !isSending &&
      !isRevealing &&
      !pendingQuestion
    ) {
      invalidateRedoSuffixes();
    }
  }, [
    nodes,
    edges,
    currentSerializedData,
    isSending,
    isRevealing,
    pendingQuestion,
    invalidateRedoSuffixes,
  ]);

  /**
   * Runs the whole upfront Gate-2 validate/correction loop for a proposed
   * plan, before anything is shown to the user. On success, applies the
   * plan directly to the canvas (via onApplyPlan) -- same as any manual
   * edit, no review step -- and replaces the streaming placeholder turn
   * (`turnId`) with a plan_proposed checklist. On failure (parse error,
   * structural failure, or Gate 2 invalid after exhausting corrections),
   * replaces it with an error turn and never calls onApplyPlan -- nothing
   * is ever applied for a plan that can't be made valid.
   *
   * Every exit path replaces the turn via updateTurn, never a return value
   * -- handleSend never performs its own closing swap for this call,
   * regardless of outcome, so a later milestone's in-progress row updates
   * (mid-reveal) can never be clobbered by a stale positional splice.
   *
   * Valid operations are grouped into dependency-safe cards and revealed one
   * at a time. Each completed card records its own checkpoint.
   */
  const runPlanTurn = useCallback(
    async (
      outcome: AgentChatTurnResponse,
      token: string,
      sessionIdForCorrection: string,
      turnId: string,
      latencyTrace?: CopilotLatencyTrace,
      preserveCompletedPlan = false
    ): Promise<void> => {
      let current = outcome;
      if (outcome.authoring_schema)
        authoringSchemaRef.current = bindAuthoringSchema(
          outcome.authoring_schema,
          outcome.catalog_hash
        );
      if (
        outcome.catalog_hash &&
        authoringSchemaRef.current &&
        outcome.catalog_hash !== authoringSchemaRef.current.catalog_hash
      )
        throw new Error(
          'The Copilot candidate belongs to a different catalog. Reload the workflow.'
        );
      const turnEpoch = requestEpochRef.current;
      const assertCurrentTurn = () => {
        if (turnEpoch !== requestEpochRef.current) {
          throw new DOMException('Stale Copilot response', 'AbortError');
        }
      };
      const pauseIssuedCandidateForWorkerUpdate = () => {
        const candidate = current;
        setWorkerUpdating(true);
        workerRetryRef.current = () => {
          setWorkerUpdating(false);
          setIsSending(true);
          const controller = new AbortController();
          streamAbortRef.current = controller;
          const retryEpoch = ++requestEpochRef.current;
          const _ = runPlanTurnRef
            .current?.(
              candidate,
              token,
              sessionIdForCorrection,
              turnId,
              undefined,
              preserveCompletedPlan
            )
            .catch((retryError: unknown) => {
              if (retryEpoch !== requestEpochRef.current) return;
              showTurnError(
                turnId,
                retryError instanceof Error
                  ? retryError.message
                  : 'Failed to validate the proposed changes.'
              );
            })
            .finally(() => {
              if (retryEpoch !== requestEpochRef.current) return;
              setIsSending(false);
              streamAbortRef.current = null;
            });
        };
      };
      latencyTrace?.mark('server_outcome_received', {
        outcome_kind: outcome.kind,
        operation_count: outcome.ops?.length ?? 0,
      });

      for (let round = 0; round < MAX_CLIENT_CORRECTION_ROUNDS; round++) {
        if (current.session_state_version !== undefined) {
          sessionStateVersionRef.current = current.session_state_version;
          setSessionStateVersion(current.session_state_version);
        }
        if (current.draft_id) {
          setActiveDraftId(current.draft_id);
        }
        if (current.configuration_node_states) {
          onConfigurationLifecycleChange(current.configuration_node_states);
        }
        if (
          current.kind !== 'plan_proposed' &&
          current.kind !== 'draft_segment'
        ) {
          if (preserveCompletedPlan && current.question) {
            setPendingQuestion(current.question);
            return;
          }
          // The optimistic acknowledgement describes work about to be
          // drafted. If the model asks a clarification or fails before
          // producing a plan, withdraw it instead of claiming work started.
          settleAcknowledgement(turnId, null);
          if (current.topology_clarification_id) {
            setPendingTopologyClarificationId(
              current.topology_clarification_id
            );
          }
          if (current.question) setPendingQuestion(current.question);
          if (current.kind === 'error') {
            showTurnError(
              turnId,
              current.message || 'Copilot did not respond.'
            );
          } else {
            updateTurn(turnId, () => ({
              id: turnId,
              role: 'assistant',
              kind: 'clarifying_question',
              content: current.question
                ? null
                : current.message || 'Copilot did not respond.',
            }));
          }
          return;
        }
        setPendingTopologyClarificationId(null);

        // A V2 question-only segment has no graph to validate or reveal. This
        // is especially important for a brand-new empty workflow, whose
        // current graph correctly fails workflow validation until the answer
        // gives Copilot enough information to draft the first node.
        if (
          current.kind === 'draft_segment' &&
          (current.ops?.length ?? 0) === 0 &&
          current.draft_status === 'awaiting_answer' &&
          current.question
        ) {
          setPendingQuestion(
            await acknowledgeSegment(
              current,
              token,
              sessionIdForCorrection,
              nodesRef.current,
              edgesRef.current,
              new Map(),
              latencyTrace
            )
          );
          updateTurn(turnId, () => ({
            id: turnId,
            role: 'assistant',
            kind: 'clarifying_question',
            content: null,
          }));
          return;
        }

        let parsedOps;
        let preview;
        const endPlanPreparation = latencyTrace?.startPhase(
          'plan_parse_and_apply',
          { correction_round: round + 1 }
        );
        try {
          const graphOpsVersion = readGraphOpsVersion(
            current.graph_ops_version
          );
          parsedOps =
            current.draft_protocol_version === 2 &&
            (current.phase === 'configuration_segment_issued' ||
              current.phase === 'edit_segment_issued')
              ? parseCompilerRawOps(current.ops ?? [], graphOpsVersion)
              : parseRawOps(current.ops ?? [], graphOpsVersion);
          preview = applyGraphOps(
            parsedOps,
            nodesRef.current,
            edgesRef.current,
            {
              recordSteps: true,
              graphOpsVersion,
              authoringSchema: authoringSchemaRef.current,
              catalogHash:
                current.catalog_hash ??
                authoringSchemaRef.current?.catalog_hash,
            }
          );
        } catch (err) {
          showTurnError(
            turnId,
            err instanceof RawOpParseError
              ? `The proposed plan was malformed: ${err.message}`
              : 'The proposed plan could not be parsed.'
          );
          return;
        } finally {
          endPlanPreparation?.();
        }

        if (preview.kind !== 'success') {
          showTurnError(
            turnId,
            `This plan can't be applied to the current graph: ${preview.reason}`
          );
          return;
        }

        let validation: ValidateAgentGraphResponse;
        const endGate2 = latencyTrace?.startPhase('gate2_validation', {
          correction_round: round + 1,
        });
        try {
          const validationParams = {
            workflowId,
            serializedData: currentSerializedData(preview.nodes, preview.edges),
            ...(current.draft_protocol_version === 2
              ? {
                  sessionId: sessionIdForCorrection,
                  draftId: current.draft_id,
                  segmentId: current.segment_id,
                  candidateAttemptId: current.candidate_attempt_id,
                  phaseClaimVersion: current.phase_claim_version,
                  refMap: Object.fromEntries(preview.refMap),
                }
              : {}),
          };
          let throttleRetryCount = 0;
          while (true) {
            try {
              validation = await validateAgentGraph(
                token,
                validationParams,
                streamAbortRef.current?.signal
              );
              break;
            } catch (error) {
              if (
                error instanceof AgentApiError &&
                error.code === 'validation_throttled' &&
                throttleRetryCount === 0
              ) {
                throttleRetryCount += 1;
                await waitForRetry(
                  error.retryAfterMs ?? 1000,
                  streamAbortRef.current?.signal
                );
                continue;
              }
              throw error;
            }
          }
          if (
            current.draft_protocol_version === 2 &&
            (validation.candidate_attempt_id !== current.candidate_attempt_id ||
              !validation.candidate_fingerprint)
          ) {
            throw new AgentApiError(
              'Copilot returned validation for a stale candidate.',
              'stale_candidate_attempt'
            );
          }
          assertCurrentTurn();
        } catch (error) {
          if (
            error instanceof AgentApiError &&
            error.status === 409 &&
            error.code === 'worker_catalog_incompatible'
          ) {
            pauseIssuedCandidateForWorkerUpdate();
            return;
          }
          showTurnError(
            turnId,
            error instanceof Error
              ? error.message
              : 'Failed to validate the proposed changes.'
          );
          return;
        } finally {
          endGate2?.();
        }

        const failureCodeCounts = validation.errors.reduce<
          Record<string, number>
        >((counts, error) => {
          counts[error.code] = (counts[error.code] ?? 0) + 1;
          return counts;
        }, {});
        latencyTrace?.mark('gate2_result', {
          correction_round: round + 1,
          is_valid: validation.is_valid,
          error_count: validation.errors.length,
          blocking_error_count: validation.errors.filter(
            (error) => error.blocking
          ).length,
        });
        Object.entries(failureCodeCounts).forEach(([code, count]) => {
          latencyTrace?.mark('gate2_failure_code', {
            correction_round: round + 1,
            validation_code: code,
            count,
          });
        });

        if (validation.is_valid) {
          const opSteps = preview.steps ?? [];
          const planSteps = groupOpsIntoSteps(parsedOps, opSteps);

          // The topology segment is the user-visible build. Its automatic
          // configuration continuation updates those already-visible nodes;
          // replacing the topology checklist with configuration-only rows
          // makes the graph appear to be rebuilt or revalidated. Apply and
          // persist the configuration atomically, then leave the completed
          // topology turn in place and surface only the resulting question.
          if (
            preserveCompletedPlan &&
            current.draft_protocol_version === 2 &&
            current.phase === 'configuration_segment_issued'
          ) {
            const configuredValues = new Map<
              string,
              { value: string | undefined; order: number }
            >();
            planSteps.forEach((step, order) => {
              if (!step.nodeId) return;
              configuredValues.set(step.nodeId, {
                value: describeStep(step, parsedOps, opSteps).value,
                order,
              });
            });
            const valueRevealDelay = planRevealStepDelayMs(
              configuredValues.size
            );
            onApplyPlan(preview.nodes, preview.edges, {
              animateNodeIds: [...configuredValues.keys()],
            });
            const handshake = await performSegmentHandshake(
              {
                outcome: current,
                sessionId: sessionIdForCorrection,
                baseNodes: nodesRef.current,
                baseEdges: edgesRef.current,
                finalNodes: preview.nodes,
                finalEdges: preview.edges,
                refMap: preview.refMap,
                receiptRequestId: uuidv4(),
                persisted: null,
                validatedFingerprint: validation.candidate_fingerprint,
              },
              token,
              streamAbortRef.current?.signal,
              true,
              latencyTrace
            );
            assertCurrentTurn();
            onReconcileSegmentGraph(preview.nodes, preview.edges);
            nodesRef.current = preview.nodes;
            edgesRef.current = preview.edges;
            refreshLatestTurnCheckpoints(preview.nodes);
            // Configuration mutates nodes already represented by the topology
            // checklist. Refresh those rows' value lines in place and remove
            // the answer request's temporary Drafting turn instead of showing
            // a second stack of cards for the same nodes.
            setLiveTurns((previous) => {
              let planIndex = -1;
              for (let index = previous.length - 1; index >= 0; index -= 1) {
                const turn = previous[index];
                if (
                  turn.role === 'assistant' &&
                  turn.kind === 'plan_proposed'
                ) {
                  planIndex = index;
                  break;
                }
              }
              return previous.flatMap((turn, index) => {
                if (turn.id === turnId && index !== planIndex) return [];
                if (
                  index !== planIndex ||
                  turn.role !== 'assistant' ||
                  turn.kind !== 'plan_proposed'
                ) {
                  return [turn];
                }
                return [
                  {
                    ...turn,
                    rows: turn.rows.map((row) => {
                      const configured = row.nodeId
                        ? configuredValues.get(row.nodeId)
                        : undefined;
                      return configured
                        ? {
                            ...row,
                            value: configured.value,
                            valueRevealKey: uuidv4(),
                            valueRevealDelayMs:
                              configured.order * valueRevealDelay,
                          }
                        : row;
                    }),
                  },
                ];
              });
            });
            setPendingQuestion(handshake?.question ?? null);
            return;
          }

          const baseCheckpointId = addCheckpoint({
            label: 'Before Copilot changes',
            turnId,
            phaseIndex: -1,
            nodes: nodesRef.current,
            edges: edgesRef.current,
          });

          // A degenerate/empty plan (no ops -- e.g. a no-op turn) has
          // nothing to reveal; apply it directly, same as before this
          // milestone. Every other plan goes through the reveal below.
          if (planSteps.length === 0) {
            const intro =
              current.intent_summary ||
              current.summary ||
              draftIntentRef.current ||
              'the workflow below';
            settleAcknowledgement(turnId, intro);
            onApplyPlan(preview.nodes, preview.edges);
            updateTurn(turnId, () => ({
              id: turnId,
              role: 'assistant',
              kind: 'plan_proposed',
              intro,
              showIntro: !acknowledgementTurnIdsRef.current.has(turnId),
              answeredQuestions: [],
              rows: [],
              baseCheckpointId,
              isComplete: true,
            }));
            if (
              current.draft_protocol_version === 2 &&
              current.draft_id &&
              current.segment_id
            ) {
              const handshake = await performSegmentHandshake(
                {
                  outcome: current,
                  sessionId: sessionIdForCorrection,
                  baseNodes: nodesRef.current,
                  baseEdges: edgesRef.current,
                  finalNodes: preview.nodes,
                  finalEdges: preview.edges,
                  refMap: preview.refMap,
                  receiptRequestId: uuidv4(),
                  persisted: null,
                  validatedFingerprint: validation.candidate_fingerprint,
                },
                token,
                streamAbortRef.current?.signal,
                true,
                latencyTrace
              );
              setPendingQuestion(handshake?.question ?? null);
              if (handshake) {
                const awaitsConfiguration =
                  handshake.acknowledgement.continue_draft ||
                  handshake.acknowledgement.draft_status === 'configuring';
                if (awaitsConfiguration) {
                  updateTurn(turnId, (turn) =>
                    turn.role === 'assistant' && turn.kind === 'plan_proposed'
                      ? { ...turn, statusLabel: 'Drafting questions' }
                      : turn
                  );
                }
                try {
                  const continuation = await continueAfterTopologyReceipt(
                    handshake,
                    token,
                    sessionIdForCorrection,
                    preview.nodes,
                    preview.edges,
                    streamAbortRef.current?.signal,
                    latencyTrace
                  );
                  if (continuation?.kind === 'configuration_prepared') {
                    setPendingQuestion(continuation.question ?? null);
                  } else if (continuation) {
                    await runPlanTurnRef.current?.(
                      continuation,
                      token,
                      sessionIdForCorrection,
                      turnId,
                      latencyTrace,
                      true
                    );
                  }
                } finally {
                  updateTurn(turnId, (turn) =>
                    turn.role === 'assistant' && turn.kind === 'plan_proposed'
                      ? { ...turn, statusLabel: undefined }
                      : turn
                  );
                }
              }
            } else {
              setPendingQuestion(
                await acknowledgeSegment(
                  current,
                  token,
                  sessionIdForCorrection,
                  preview.nodes,
                  preview.edges,
                  preview.refMap,
                  latencyTrace
                )
              );
            }
            return;
          }

          const rows: PlanRow[] = planSteps.map((step): PlanRow => ({
            id: uuidv4(),
            kind: 'step',
            ...describeStep(step, parsedOps, opSteps),
            nodeId: step.nodeId,
            completed: false,
            checkpointId: undefined,
          }));

          const intro =
            current.intent_summary ||
            current.summary ||
            draftIntentRef.current ||
            'the workflow below';
          settleAcknowledgement(turnId, intro);
          updateTurn(turnId, () => ({
            id: turnId,
            role: 'assistant',
            kind: 'plan_proposed',
            intro,
            showIntro: !acknowledgementTurnIdsRef.current.has(turnId),
            answeredQuestions: [],
            rows,
            baseCheckpointId,
            isComplete: false,
          }));

          const revealSteps: RevealStepInput[] = planSteps.map((step, i) => {
            const lastOpIndex = step.opIndices[step.opIndices.length - 1];
            const opStep = opSteps[lastOpIndex];
            // New nodes and existing nodes whose displayed configuration
            // changed use the same canvas fade/rise. The Set keeps a folded
            // add_node + configure_node step from animating its node twice.
            const enteringNodeIds = [
              ...new Set([...step.addedNodeIds, ...step.modifiedNodeIds]),
            ];
            const enteringEdgeIds = step.opIndices.flatMap(
              (opIndex) => opSteps[opIndex]?.addedEdgeIds ?? []
            );
            return {
              nodes: opStep.nodes,
              edges: opStep.edges,
              enteringNodeIds,
              enteringEdgeIds,
              phaseIndex: i,
              isPhaseBoundary: true,
            };
          });

          // Protocol-1 segments already have one immutable, Gate-2-valid
          // final snapshot. Dispatch its exact save before presentation and
          // let persistence/receipt run independently from the visual reveal.
          // The settled wrapper prevents an aborted reveal from leaving an
          // early handshake rejection unobserved.
          let earlyHandshake: Promise<
            | {
                question: AgentStructuredQuestion | null;
                acknowledgement: AgentChatTurnResponse | null;
                error?: never;
              }
            | {
                question?: never;
                acknowledgement?: never;
                error: unknown;
              }
          > | null = null;
          let earlyContinuation: Promise<AgentChatTurnResponse | null> | null =
            null;
          if (
            (current.draft_protocol_version === 1 ||
              current.draft_protocol_version === 2) &&
            current.draft_id &&
            current.segment_id
          ) {
            const recovery: SegmentRecovery = {
              outcome: current,
              sessionId: sessionIdForCorrection,
              baseNodes: nodesRef.current,
              baseEdges: edgesRef.current,
              finalNodes: preview.nodes,
              finalEdges: preview.edges,
              refMap: preview.refMap,
              receiptRequestId: uuidv4(),
              persisted: null,
              validatedFingerprint: validation.candidate_fingerprint,
            };
            const initialPersistPromise = persistSegmentSnapshot(
              recovery,
              1,
              latencyTrace
            );
            earlyHandshake = performSegmentHandshake(
              recovery,
              token,
              streamAbortRef.current?.signal,
              true,
              latencyTrace,
              initialPersistPromise
            ).then(
              (result) => ({
                question: result?.question ?? null,
                acknowledgement: result?.acknowledgement ?? null,
              }),
              (error: unknown) => ({ error })
            );
            if (
              current.draft_protocol_version === 2 &&
              current.phase === 'topology_segment_issued'
            ) {
              earlyContinuation = earlyHandshake.then(async (handshake) => {
                if ('error' in handshake) throw handshake.error;
                if (!handshake.acknowledgement) return null;
                return continueAfterTopologyReceipt(
                  {
                    question: handshake.question ?? null,
                    acknowledgement: handshake.acknowledgement,
                  },
                  token,
                  sessionIdForCorrection,
                  preview.nodes,
                  preview.edges,
                  streamAbortRef.current?.signal,
                  latencyTrace
                );
              });
              // Reveal cancellation can abandon this overlap path. Attach a
              // rejection observer now; the original promise still carries
              // the error to the normal post-reveal await when applicable.
              void earlyContinuation.catch((_error: unknown): null => null);
            }
          }

          // Commit the acknowledgement on its own browser turn. The first
          // canvas/card step can still begin immediately afterward, but it
          // can no longer win the same React batch and appear before the
          // acknowledgement text.
          await waitForRetry(0, streamAbortRef.current?.signal);
          assertCurrentTurn();

          const endReveal = latencyTrace?.startPhase('canvas_reveal', {
            step_count: revealSteps.length,
          });
          const revealGeneration = ++revealGenerationRef.current;
          try {
            await onStartReveal({
              steps: revealSteps,
              onStepApplied: (stepIndex) => {
                if (
                  turnEpoch !== requestEpochRef.current ||
                  revealGeneration !== revealGenerationRef.current
                ) {
                  return;
                }
                latencyTrace?.mark('canvas_reveal_step', {
                  step_index: stepIndex,
                });
                updateTurn(turnId, (t) =>
                  t.role === 'assistant' && t.kind === 'plan_proposed'
                    ? {
                        ...t,
                        rows: t.rows.map((row, i) =>
                          i === stepIndex ? { ...row, completed: true } : row
                        ),
                      }
                    : t
                );
              },
              onPhaseComplete: (phaseIndex, phaseNodes, phaseEdges) => {
                if (
                  turnEpoch !== requestEpochRef.current ||
                  revealGeneration !== revealGenerationRef.current
                ) {
                  return;
                }
                const checkpointId = addCheckpoint({
                  label: rows[phaseIndex].title,
                  turnId,
                  phaseIndex,
                  nodes: phaseNodes,
                  edges: phaseEdges,
                });
                updateTurn(turnId, (t) =>
                  t.role === 'assistant' && t.kind === 'plan_proposed'
                    ? {
                        ...t,
                        rows: t.rows.map((row, i) =>
                          i === phaseIndex ? { ...row, checkpointId } : row
                        ),
                      }
                    : t
                );
              },
            });
          } finally {
            endReveal?.();
          }
          assertCurrentTurn();
          if (revealGeneration !== revealGenerationRef.current) {
            throw new DOMException('Stale Copilot reveal', 'AbortError');
          }

          updateTurn(turnId, (t) =>
            t.role === 'assistant' && t.kind === 'plan_proposed'
              ? { ...t, isComplete: true }
              : t
          );
          if (earlyHandshake) {
            const handshake = await earlyHandshake;
            if ('error' in handshake) throw handshake.error;
            assertCurrentTurn();
            if (revealGeneration !== revealGenerationRef.current) {
              throw new DOMException('Stale Copilot reveal', 'AbortError');
            }
            // The exact graph is now both saved and receipt-acknowledged.
            // Re-assert that authoritative snapshot after presentation so a
            // late React Flow measurement/render update cannot leave the
            // canvas on a visually incomplete reveal frame. This is a
            // no-network reconciliation and also clears any stale unload
            // fallback for the same acknowledged bytes.
            onReconcileSegmentGraph(preview.nodes, preview.edges);
            nodesRef.current = preview.nodes;
            edgesRef.current = preview.edges;
            configurationExecutionModeRef.current =
              handshake.acknowledgement?.configuration_execution_mode ??
              'serial';
            setPendingQuestion(handshake.question);
            if (
              current.draft_protocol_version === 2 &&
              handshake.acknowledgement &&
              (handshake.acknowledgement.continue_draft ||
                handshake.acknowledgement.draft_status === 'configuring')
            ) {
              updateTurn(turnId, (turn) =>
                turn.role === 'assistant' && turn.kind === 'plan_proposed'
                  ? { ...turn, statusLabel: 'Drafting questions' }
                  : turn
              );
              try {
                const continuation = earlyContinuation
                  ? await earlyContinuation
                  : await continueAfterTopologyReceipt(
                      {
                        question: handshake.question ?? null,
                        acknowledgement: handshake.acknowledgement,
                      },
                      token,
                      sessionIdForCorrection,
                      preview.nodes,
                      preview.edges,
                      streamAbortRef.current?.signal,
                      latencyTrace
                    );
                if (continuation?.kind === 'configuration_prepared') {
                  setPendingQuestion(continuation.question ?? null);
                } else if (continuation) {
                  await runPlanTurnRef.current?.(
                    continuation,
                    token,
                    sessionIdForCorrection,
                    turnId,
                    latencyTrace,
                    true
                  );
                }
              } finally {
                updateTurn(turnId, (turn) =>
                  turn.role === 'assistant' && turn.kind === 'plan_proposed'
                    ? { ...turn, statusLabel: undefined }
                    : turn
                );
              }
            }
          } else {
            setPendingQuestion(
              await acknowledgeSegment(
                current,
                token,
                sessionIdForCorrection,
                preview.nodes,
                preview.edges,
                preview.refMap,
                latencyTrace
              )
            );
          }
          assertCurrentTurn();
          return;
        }

        // Gate 2 can return a mix of blocking and deferred errors. Only the
        // former explain a rejection; deferred topology configuration errors
        // must not be fed back to the model as correction instructions.
        const blockingErrors = validation.errors.filter((e) => e.blocking);

        if (current.draft_protocol_version === 2) {
          const correctableEditCodes = new Set([
            'cycle',
            'duplicate_edge',
            'invalid_branch',
            'invalid_root',
            'multiple_parents',
            'required_field',
            'structural_error',
          ]);
          const canCorrectEdit =
            round < MAX_CLIENT_CORRECTION_ROUNDS - 1 &&
            current.phase === 'edit_segment_issued' &&
            Boolean(
              current.draft_id &&
              current.segment_id &&
              current.candidate_attempt_id &&
              current.phase_claim_version !== undefined
            ) &&
            blockingErrors.length > 0 &&
            blockingErrors.every((error) =>
              correctableEditCodes.has(error.code)
            );
          if (canCorrectEdit) {
            const endCorrection = latencyTrace?.startPhase('gate2_correction', {
              correction_round: round + 1,
            });
            try {
              current = await streamAgentMessage(
                token,
                {
                  sessionId: sessionIdForCorrection,
                  workflowId,
                  serializedData: currentSerializedData(
                    nodesRef.current,
                    edgesRef.current
                  ),
                  clientRequestId: uuidv4(),
                  draftProtocolVersion: 2,
                  workflowGraphRevision: graphRevisionRef.current,
                  correction: {
                    kind: 'edit_correction',
                    draftId: current.draft_id!,
                    segmentId: current.segment_id!,
                    candidateAttemptId: current.candidate_attempt_id!,
                    phaseClaimVersion: current.phase_claim_version!,
                    validationCodes: blockingErrors.map((error) => error.code),
                  },
                },
                () => undefined,
                streamAbortRef.current?.signal
              );
            } catch (error) {
              if (
                error instanceof AgentApiError &&
                error.status === 409 &&
                error.code === 'worker_catalog_incompatible'
              ) {
                pauseIssuedCandidateForWorkerUpdate();
                return;
              }
              throw error;
            } finally {
              endCorrection?.();
            }
            assertCurrentTurn();
            continue;
          }
          const detail = blockingErrors.map((e) => e.message).join('; ');
          const validationMessage = `Copilot couldn't validate this draft${
            detail ? `: ${detail}.` : '.'
          }`;
          try {
            await cancelRejectedV2Candidate(
              token,
              sessionIdForCorrection,
              current,
              streamAbortRef.current?.signal
            );
            showTurnError(turnId, `${validationMessage} Please try again.`);
          } catch {
            await refreshV2RecoveryStatus(
              token,
              sessionIdForCorrection,
              streamAbortRef.current?.signal
            );
            showTurnError(
              turnId,
              `${validationMessage} Stop drafting before trying again.`
            );
          }
          return;
        }

        if (round === MAX_CLIENT_CORRECTION_ROUNDS - 1) {
          showTurnError(
            turnId,
            `Couldn't produce a valid plan: ${blockingErrors
              .map((e) => e.message)
              .join('; ')}`
          );
          return;
        }

        const endCorrection = latencyTrace?.startPhase('gate2_correction', {
          correction_round: round + 1,
        });
        try {
          current = await sendAgentCorrection(
            token,
            {
              sessionId: sessionIdForCorrection,
              workflowId,
              serializedData: currentSerializedData(
                nodesRef.current,
                edgesRef.current
              ),
              correctionErrors: blockingErrors.map((e) => e.message),
              draftProtocolVersion: current.draft_protocol_version,
              workflowGraphRevision: graphRevisionRef.current,
              expectedSessionStateVersion:
                current.session_state_version ?? sessionStateVersionRef.current,
              clientRequestId: uuidv4(),
            },
            streamAbortRef.current?.signal
          );
        } catch (error) {
          if (
            error instanceof AgentApiError &&
            error.status === 409 &&
            error.code === 'worker_catalog_incompatible'
          ) {
            pauseIssuedCandidateForWorkerUpdate();
            return;
          }
          throw error;
        } finally {
          endCorrection?.();
        }
        assertCurrentTurn();
      }

      // Unreachable: every path through the loop above returns.
      showTurnError(turnId, 'Copilot could not produce a valid plan.');
    },
    [
      workflowId,
      currentSerializedData,
      onApplyPlan,
      acknowledgeSegment,
      performSegmentHandshake,
      persistSegmentSnapshot,
      onReconcileSegmentGraph,
      onStartReveal,
      addCheckpoint,
      refreshLatestTurnCheckpoints,
      updateTurn,
      settleAcknowledgement,
      showTurnError,
      continueAfterTopologyReceipt,
      cancelRejectedV2Candidate,
      refreshV2RecoveryStatus,
      onConfigurationLifecycleChange,
    ]
  );

  runPlanTurnRef.current = runPlanTurn;

  // A reload can resume a v2 draft after topology was acknowledged but
  // before configuration finished. Continue that server-owned phase without
  // inventing another user message or another persisted conversation turn.
  useEffect(() => {
    if (
      !open ||
      !resumableConfiguration ||
      !sessionId ||
      isStarting ||
      isSending ||
      workerUpdating ||
      !runPlanTurnRef.current
    ) {
      return;
    }
    const token = getToken();
    if (!token) return;
    setResumableConfiguration(false);
    setIsSending(true);
    const controller = new AbortController();
    streamAbortRef.current = controller;
    const epoch = ++requestEpochRef.current;
    const turnId = uuidv4();
    setLiveTurns((previous) => [
      ...previous,
      { id: turnId, role: 'assistant', kind: 'streaming' },
    ]);
    void streamDraftContinuation(
      token,
      sessionId,
      nodesRef.current,
      edgesRef.current,
      controller.signal,
      undefined,
      resumableConfigurationStageRef.current
    )
      .then(async (outcome) => {
        if (outcome.kind === 'configuration_prepared' && !outcome.question) {
          resumableConfigurationStageRef.current = 'finalize';
          outcome = await streamDraftContinuation(
            token,
            sessionId,
            nodesRef.current,
            edgesRef.current,
            controller.signal
          );
        }
        await runPlanTurnRef.current?.(outcome, token, sessionId, turnId);
      })
      .catch((error) => {
        if (epoch !== requestEpochRef.current) return;
        if (
          error instanceof AgentApiError &&
          error.status === 409 &&
          error.code === 'worker_catalog_incompatible'
        ) {
          setLiveTurns((previous) =>
            previous.filter((turn) => turn.id !== turnId)
          );
          pauseConfigurationForWorkerUpdate(
            resumableConfigurationStageRef.current
          );
          return;
        }
        const _ = refreshV2RecoveryStatus(token, sessionId, controller.signal);
        showTurnError(
          turnId,
          error instanceof Error
            ? error.message
            : 'Copilot could not resume configuration.'
        );
      })
      .finally(() => {
        if (epoch === requestEpochRef.current) {
          setIsSending(false);
          streamAbortRef.current = null;
        }
      });
  }, [
    open,
    resumableConfiguration,
    sessionId,
    isStarting,
    isSending,
    workerUpdating,
    getToken,
    streamDraftContinuation,
    refreshV2RecoveryStatus,
    showTurnError,
    pauseConfigurationForWorkerUpdate,
  ]);

  // Revert click: getCheckpoint deserializes the checkpoint's structural
  // bytes and returns them alongside its stored position map;
  // onRestoreSnapshot (handleRestoreAgentSnapshot in LBCanvas.tsx) cancels
  // any in-flight reveal itself before applying them -- an earlier turn's
  // checkpoint rows stay clickable while a later turn is mid-reveal, and
  // clicking one must not let that reveal's next tick immediately
  // overwrite the just-restored canvas state.
  const handleToggleStep = useCallback(
    (turnId: string, rowIndex: number) => {
      if (activeDraftId || pendingQuestion || isSending || isRevealing) return;
      const turn = liveTurns.find(
        (candidate) =>
          candidate.id === turnId &&
          candidate.role === 'assistant' &&
          candidate.kind === 'plan_proposed'
      );
      if (!turn || turn.role !== 'assistant' || turn.kind !== 'plan_proposed') {
        return;
      }
      const row = turn.rows[rowIndex];
      if (!row) return;
      // Incremental edits start from a non-empty graph. Never let an old
      // whole-graph checkpoint erase manual changes or a later Copilot turn:
      // undo/redo is valid only while the canvas still matches one of this
      // turn's own checkpoints.
      const currentBytes = currentSerializedData(
        nodesRef.current,
        edgesRef.current
      );
      const knownCheckpointIds = [
        turn.baseCheckpointId,
        ...turn.rows.map((candidateRow) => candidateRow.checkpointId),
      ].filter((id): id is string => Boolean(id));
      const isOnTurnCheckpoint = knownCheckpointIds.some((checkpointId) => {
        const checkpoint = getCheckpoint(checkpointId);
        return (
          checkpoint !== null &&
          currentSerializedData(checkpoint.nodes, checkpoint.edges) ===
            currentBytes
        );
      });
      if (!isOnTurnCheckpoint) return;
      // Every row checkpoint is the complete graph immediately after that
      // step, not an isolated patch. A forward jump therefore restores all
      // preceding dependencies atomically and represents a graph that really
      // existed during the original reveal.
      const targetCursor = row.completed ? rowIndex - 1 : rowIndex;
      const checkpointId =
        targetCursor < 0
          ? turn.baseCheckpointId
          : turn.rows[targetCursor]?.checkpointId;
      if (!checkpointId) return;
      const restored = getCheckpoint(checkpointId);
      if (!restored) return;
      restoringCheckpointBytesRef.current = currentSerializedData(
        restored.nodes,
        restored.edges
      );
      onRestoreSnapshot(restored.nodes, restored.edges, restored.positions);
      updateTurn(turnId, (candidate) =>
        candidate.role === 'assistant' && candidate.kind === 'plan_proposed'
          ? {
              ...candidate,
              rows: candidate.rows.map((candidateRow, index) => ({
                ...candidateRow,
                completed: index <= targetCursor,
              })),
            }
          : candidate
      );
    },
    [
      activeDraftId,
      pendingQuestion,
      isSending,
      isRevealing,
      liveTurns,
      getCheckpoint,
      currentSerializedData,
      onRestoreSnapshot,
      updateTurn,
    ]
  );

  const handleFileSelected = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file) return;

      if (!ATTACHMENT_ACCEPT.split(',').includes(file.type)) {
        setAttachError('Only PNG/JPEG/GIF/WEBP images or a PDF are supported.');
        return;
      }
      if (file.size > MAX_ATTACHMENT_BYTES) {
        setAttachError(
          `File exceeds the maximum size of ${MAX_ATTACHMENT_BYTES / (1024 * 1024)}MB.`
        );
        return;
      }

      try {
        const dataBase64 = await readFileAsBase64(file);
        setAttachError(null);
        setAttachedFile({ file, dataBase64 });
      } catch {
        setAttachError('Failed to read the attached file. Please try again.');
      }
    },
    []
  );

  const handleRemoveAttachment = useCallback(() => {
    setAttachedFile(null);
    setAttachError(null);
  }, []);

  const handleSubmitAnswer = useCallback(
    async (values: string[], templateChunks?: TextTemplateChunk[]) => {
      if (
        !pendingQuestion ||
        !sessionId ||
        isSending ||
        (values.length === 0 && !templateChunks)
      ) {
        return;
      }
      const token = getToken();
      if (!token) return;
      const originalInput = inputValue;
      setRecoveredAnswer(null);
      setWorkerUpdating(false);
      const labelsByValue = new Map(
        (displayedQuestion ?? pendingQuestion).choices.map((choice) => [
          choice.value,
          choice.label,
        ])
      );
      const templateLabel = templateChunks
        ?.map((chunk) => {
          if (chunk.text !== undefined) return chunk.text;
          if (!chunk.variable) return '';
          const name = chunk.variable.derived_variable_name
            ? `${chunk.variable.variable_name}.${chunk.variable.derived_variable_name}`
            : chunk.variable.variable_name;
          return `{{${chunk.variable.node_id}.${name}}}`;
        })
        .join('');
      const labels = templateChunks
        ? [templateLabel ?? 'Template']
        : values.map((value) => labelsByValue.get(value) ?? value);
      const questionPrompt = (displayedQuestion ?? pendingQuestion).prompt;
      const questionBeingAnswered = displayedQuestion ?? pendingQuestion;
      const turnId = uuidv4();
      const answerId = uuidv4();
      const epoch = ++requestEpochRef.current;
      let answerAccepted = false;
      let answerRecorded = false;
      setIsSubmittingAnswer(true);
      setDepartingQuestion({ question: questionBeingAnswered });
      setLiveTurns((previous) => [
        ...previous,
        { id: turnId, role: 'assistant', kind: 'streaming' },
      ]);
      if (answerHandoffTimerRef.current !== null) {
        window.clearTimeout(answerHandoffTimerRef.current);
      }
      const handoffDelayMs = window.matchMedia?.(
        '(prefers-reduced-motion: reduce)'
      ).matches
        ? 0
        : QUESTION_ANSWER_HANDOFF_MS;
      // Commit accepted answers independently of the exit animation. A
      // continuation retry can advance the request epoch before it ends.
      const recordAcceptedAnswer = () => {
        if (answerRecorded || epoch !== requestEpochRef.current) return;
        answerRecorded = true;
        setLiveTurns((previous) => {
          let planIndex = -1;
          for (let index = previous.length - 1; index >= 0; index -= 1) {
            const turn = previous[index];
            if (turn.role === 'assistant' && turn.kind === 'plan_proposed') {
              planIndex = index;
              break;
            }
          }
          const newerStandalonePrompt = previous
            .slice(planIndex + 1)
            .some((turn) => turn.role === 'user' && !turn.answerQuestionPrompt);
          if (planIndex >= 0 && !newerStandalonePrompt) {
            return previous.map((turn, index) =>
              index === planIndex &&
              turn.role === 'assistant' &&
              turn.kind === 'plan_proposed'
                ? {
                    ...turn,
                    answeredQuestions: [
                      ...turn.answeredQuestions,
                      {
                        id: answerId,
                        prompt: questionPrompt,
                        labels,
                        templateChunks,
                        animateIn: true,
                      },
                    ],
                  }
                : turn
            );
          }
          const streamingIndex = previous.findIndex(
            (turn) => turn.id === turnId
          );
          const answerTurn: LiveTurn = {
            id: answerId,
            role: 'user',
            content: '',
            answerChips: labels,
            answerTemplateChunks: templateChunks,
            answerQuestionPrompt: questionPrompt,
            animateIn: true,
          };
          if (streamingIndex < 0) return [...previous, answerTurn];
          return [
            ...previous.slice(0, streamingIndex),
            answerTurn,
            ...previous.slice(streamingIndex),
          ];
        });
      };
      answerHandoffTimerRef.current = window.setTimeout(() => {
        answerHandoffTimerRef.current = null;
        setDepartingQuestion(null);
      }, handoffDelayMs);
      setInputValue('');
      setIsSending(true);
      setPendingQuestion(null);
      setResolvedTeamQuestion(null);
      const controller = new AbortController();
      streamAbortRef.current = controller;
      const clientRequestId = uuidv4();
      const latencyTrace = new CopilotLatencyTrace(clientRequestId, 'answer');
      let latencyStatus = 'completed';
      try {
        const endAnswerRequest = latencyTrace.startPhase('answer_request');
        let outcome: AgentChatTurnResponse;
        try {
          outcome = await sendAgentAnswer(
            token,
            {
              sessionId,
              workflowId,
              serializedData: currentSerializedData(
                nodesRef.current,
                edgesRef.current
              ),
              questionId: pendingQuestion.question_id,
              values,
              templateChunks,
              draftProtocolVersion:
                activeDraftId || durableDraftEnabled
                  ? draftProtocolVersion
                  : undefined,
              workflowGraphRevision:
                activeDraftId || durableDraftEnabled
                  ? graphRevisionRef.current
                  : undefined,
              expectedSessionStateVersion: activeDraftId
                ? sessionStateVersionRef.current
                : undefined,
              clientRequestId,
            },
            controller.signal
          );
          answerAccepted = true;
          recordAcceptedAnswer();
        } finally {
          endAnswerRequest();
        }
        if (epoch !== requestEpochRef.current) {
          latencyStatus = 'stale';
          return;
        }
        if (
          outcome.draft_protocol_version === 2 &&
          outcome.kind === 'answer_acknowledged' &&
          (outcome.continue_draft || outcome.draft_status === 'configuring')
        ) {
          if (outcome.session_state_version !== undefined) {
            sessionStateVersionRef.current = outcome.session_state_version;
            setSessionStateVersion(outcome.session_state_version);
          }
          if (outcome.configuration_execution_mode) {
            configurationExecutionModeRef.current =
              outcome.configuration_execution_mode;
          }
          let preparationOutcome: AgentChatTurnResponse | null = null;
          if (
            outcome.configuration_execution_mode === 'prepare_then_finalize' &&
            outcome.draft_id
          ) {
            const livePreparation = preparationTaskRef.current;
            if (livePreparation?.draftId === outcome.draft_id) {
              preparationOutcome = await joinLivePreparation(
                outcome.draft_id,
                controller.signal
              );
            } else if (outcome.preparation_status === 'running') {
              // A restored tab has no in-memory stream to join. Give a nearly
              // complete request one bounded chance to commit, then let the
              // atomic finalization claim supersede it.
              await waitForRetry(1_000, controller.signal);
            }
          }
          // A preparation that finished before this answer carries the
          // question that was current at its own commit time. Do not replay
          // that stale snapshot after the answer. Preparation does not bump
          // the main session version, so only a question committed after this
          // answer can share the answer acknowledgement's state version.
          outcome = isPostAnswerPreparationQuestionCurrent(
            preparationOutcome,
            outcome,
            pendingQuestion.question_id
          )
            ? preparationOutcome
            : await streamDraftContinuation(
                token,
                sessionId,
                nodesRef.current,
                edgesRef.current,
                controller.signal,
                latencyTrace
              );
        }
        const updatesExistingPlan =
          outcome.draft_protocol_version === 2 &&
          outcome.phase === 'configuration_segment_issued' &&
          liveTurns.some(
            (turn) => turn.role === 'assistant' && turn.kind === 'plan_proposed'
          );
        await runPlanTurn(
          outcome,
          token,
          sessionId,
          turnId,
          latencyTrace,
          updatesExistingPlan
        );
      } catch (error) {
        latencyStatus =
          error instanceof DOMException && error.name === 'AbortError'
            ? 'aborted'
            : 'failed';
        if (epoch !== requestEpochRef.current) return;
        if (!answerAccepted) {
          if (answerHandoffTimerRef.current !== null) {
            window.clearTimeout(answerHandoffTimerRef.current);
            answerHandoffTimerRef.current = null;
          }
          setDepartingQuestion(null);
          setRecoveredAnswer({
            questionId: pendingQuestion.question_id,
            values,
            templateChunks,
          });
        }
        if (
          error instanceof AgentApiError &&
          error.status === 409 &&
          error.code === 'worker_catalog_incompatible'
        ) {
          if (answerAccepted) {
            pauseConfigurationForWorkerUpdate();
            return;
          }
          setPendingQuestion(pendingQuestion);
          setInputValue(originalInput);
          setLiveTurns(liveTurns);
          setWorkerUpdating(true);
          workerRetryRef.current = () => {
            const _ = handleSubmitAnswer(values, templateChunks);
          };
          return;
        }
        const terminalized =
          draftProtocolVersion === 2
            ? await refreshV2RecoveryStatus(token, sessionId, controller.signal)
            : false;
        if (!terminalized && !answerAccepted)
          setPendingQuestion(pendingQuestion);
        updateTurn(turnId, () => ({
          id: turnId,
          role: 'assistant',
          kind: 'error',
          content:
            error instanceof Error
              ? error.message
              : 'Failed to submit the answer.',
        }));
      } finally {
        latencyTrace.finish(latencyStatus);
        if (epoch === requestEpochRef.current) {
          setIsSending(false);
          setIsSubmittingAnswer(false);
          streamAbortRef.current = null;
        }
      }
    },
    [
      pendingQuestion,
      displayedQuestion,
      sessionId,
      isSending,
      getToken,
      workflowId,
      currentSerializedData,
      runPlanTurn,
      updateTurn,
      activeDraftId,
      durableDraftEnabled,
      draftProtocolVersion,
      streamDraftContinuation,
      joinLivePreparation,
      refreshV2RecoveryStatus,
      liveTurns,
      pauseConfigurationForWorkerUpdate,
      inputValue,
    ]
  );

  const handleSend = useCallback(
    async (overrideText?: string) => {
      const text = (overrideText ?? inputValue).trim();
      if (pendingQuestion && text) {
        const values = composerAnswerValues(
          displayedQuestion ?? pendingQuestion,
          text
        );
        if (values.length === 0) {
          setAttachError('Choose one or more of the available answers.');
          return;
        }
        setAttachError(null);
        await handleSubmitAnswer(values);
        return;
      }
      if (
        (!text && !attachedFile) ||
        !sessionId ||
        isSending ||
        draftRecoveryStatus !== null ||
        (activeDraftId !== null && pendingQuestion === null)
      ) {
        return;
      }

      const token = getToken();
      if (!token) return;

      setRetiredDraftNotice(null);
      // This request branches from the current canvas, so an abandoned redo
      // suffix from an earlier turn can no longer be restored safely.
      invalidateRedoSuffixes();

      const pendingAttachment = attachedFile;
      setWorkerUpdating(false);
      const attachmentMeta: AgentAttachmentMeta | null = pendingAttachment
        ? {
            filename: pendingAttachment.file.name,
            content_type: pendingAttachment.file.type,
            size: pendingAttachment.file.size,
          }
        : null;

      const turnId = uuidv4();
      const acknowledgementId = uuidv4();
      const reduceMotion = Boolean(
        window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
      );
      if (text) draftIntentRef.current = text;
      acknowledgementTurnIdsRef.current.set(turnId, acknowledgementId);
      setLiveTurns((prev) => [
        ...prev,
        {
          id: uuidv4(),
          role: 'user',
          content: text,
          attachment: attachmentMeta,
          animateIn: true,
        },
        {
          id: acknowledgementId,
          role: 'assistant',
          kind: 'acknowledgement',
          content: text || 'the workflow described in the attachment',
          animateIn: true,
          visible: reduceMotion,
        },
        { id: turnId, role: 'assistant', kind: 'streaming' },
      ]);
      if (!reduceMotion) {
        const revealTimer = window.setTimeout(() => {
          setLiveTurns((previous) =>
            previous.map((turn) =>
              turn.id === acknowledgementId &&
              turn.role === 'assistant' &&
              turn.kind === 'acknowledgement'
                ? { ...turn, visible: true }
                : turn
            )
          );
          acknowledgementRevealTimersRef.current.delete(acknowledgementId);
        }, ACKNOWLEDGEMENT_REVEAL_DELAY_MS);
        acknowledgementRevealTimersRef.current.set(
          acknowledgementId,
          revealTimer
        );
      }
      setInputValue('');
      setAttachedFile(null);
      setAttachError(null);
      setIsSending(true);

      const controller = new AbortController();
      streamAbortRef.current = controller;
      const epoch = ++requestEpochRef.current;
      const clientRequestId = uuidv4();
      const latencyTrace = new CopilotLatencyTrace(clientRequestId, 'message');
      let latencyStatus = 'completed';

      try {
        if (durableDraftEnabled) {
          const endPreSave = latencyTrace.startPhase('pre_model_persist');
          let persisted: WorkflowGraphWriteResult | null;
          try {
            persisted = await onPersistSegment(
              nodesRef.current,
              edgesRef.current
            );
          } finally {
            endPreSave();
          }
          if (!persisted) {
            throw new Error('The current workflow could not be saved.');
          }
          graphRevisionRef.current = persisted.graph_revision;
        }
        if (epoch !== requestEpochRef.current) return;
        // The model's own thinking/tool-lookup narration is never shown --
        // the streaming turn renders as a plain "Drafting..." placeholder
        // for the whole send. Timing records only the safe event type/tool
        // name, never thinking/text content.
        const endStream = latencyTrace.startPhase('backend_stream');
        let sawFirstStreamEvent = false;
        let outcome: AgentChatTurnResponse;
        try {
          outcome = await streamAgentMessage(
            token,
            {
              sessionId,
              workflowId,
              serializedData: currentSerializedData(
                nodesRef.current,
                edgesRef.current
              ),
              message: text,
              clientRequestId,
              draftProtocolVersion: durableDraftEnabled
                ? draftProtocolVersion
                : undefined,
              workflowGraphRevision: durableDraftEnabled
                ? graphRevisionRef.current
                : undefined,
              topologyClarificationId:
                pendingTopologyClarificationId ?? undefined,
              attachment: pendingAttachment
                ? {
                    filename: pendingAttachment.file.name,
                    contentType: pendingAttachment.file.type,
                    dataBase64: pendingAttachment.dataBase64,
                  }
                : undefined,
            },
            (event) => {
              if (event.type === 'provider_progress') return;
              if (!sawFirstStreamEvent) {
                sawFirstStreamEvent = true;
                latencyTrace.mark('first_sse_event', {
                  stream_event_type: event.type,
                  tool_name:
                    event.type === 'tool_call' ||
                    event.type === 'tool_use_started'
                      ? event.name
                      : undefined,
                });
              }
            },
            controller.signal
          );
        } finally {
          endStream();
        }
        if (epoch !== requestEpochRef.current) {
          latencyStatus = 'stale';
          return;
        }
        await runPlanTurn(outcome, token, sessionId, turnId, latencyTrace);
      } catch (error) {
        latencyStatus =
          error instanceof DOMException && error.name === 'AbortError'
            ? 'aborted'
            : 'failed';
        if (epoch !== requestEpochRef.current) return;
        if (
          error instanceof AgentApiError &&
          error.status === 409 &&
          error.code === 'worker_catalog_incompatible'
        ) {
          setInputValue(text);
          setAttachedFile(pendingAttachment);
          setLiveTurns(liveTurns);
          setWorkerUpdating(true);
          workerRetryRef.current = () => {
            const _ = handleSend(text);
          };
          return;
        }
        if (draftProtocolVersion === 2) {
          await refreshV2RecoveryStatus(token, sessionId, controller.signal);
        }
        const conflictCode =
          error instanceof AgentApiError ||
          error instanceof WorkflowGraphApiError
            ? error.code
            : undefined;
        if (
          conflictCode === 'workflow_graph_conflict' ||
          conflictCode === 'workflow_not_draft'
        ) {
          setWorkflowConflict(true);
        }
        showTurnError(
          turnId,
          conflictCode === 'workflow_graph_conflict' ||
            conflictCode === 'workflow_not_draft'
            ? 'This workflow changed elsewhere. Reload it before continuing.'
            : error instanceof Error
              ? error.message
              : 'Failed to reach Copilot.'
        );
      } finally {
        latencyTrace.finish(latencyStatus);
        if (epoch === requestEpochRef.current) {
          setIsSending(false);
          streamAbortRef.current = null;
        }
      }
    },
    [
      inputValue,
      attachedFile,
      sessionId,
      isSending,
      getToken,
      workflowId,
      currentSerializedData,
      runPlanTurn,
      showTurnError,
      pendingQuestion,
      displayedQuestion,
      handleSubmitAnswer,
      invalidateRedoSuffixes,
      activeDraftId,
      durableDraftEnabled,
      onPersistSegment,
      draftProtocolVersion,
      pendingTopologyClarificationId,
      draftRecoveryStatus,
      refreshV2RecoveryStatus,
      liveTurns,
    ]
  );

  // Auto-send the empty-state's Generate/template description as the
  // session's first message, exactly once, as soon as the session is ready.
  useEffect(() => {
    if (!initialMessage || !sessionId || autoSentInitialMessageRef.current) {
      return;
    }
    autoSentInitialMessageRef.current = true;
    const _ = handleSend(initialMessage);
  }, [initialMessage, sessionId, handleSend]);

  const answerSummaries = useMemo(() => {
    const entries: QuestionAnswerEntry[] = [];
    const liveAnswerIds = new Set<string>();
    const questionPrompts = new Map<string, string>();
    for (const turn of historyTurns) {
      const question = turn.v2_outcome?.question;
      if (question) questionPrompts.set(question.question_id, question.prompt);
      if (turn.role !== 'user') continue;
      if (turn.answer_to_question) {
        entries.push({
          kind: 'answer',
          answer: {
            id: turn.id,
            prompt:
              turn.answer_to_question.prompt ??
              questionPrompts.get(turn.answer_to_question.question_id) ??
              'Question',
            labels: turn.answer_to_question.display_labels,
            templateChunks: turn.answer_to_question.template_chunks,
          },
        });
      } else {
        entries.push({ kind: 'instruction' });
      }
    }
    for (const turn of liveTurns) {
      if (turn.role === 'user') {
        if (turn.answerQuestionPrompt) {
          entries.push({
            kind: 'answer',
            answer: {
              id: turn.id,
              prompt: turn.answerQuestionPrompt,
              labels: turn.answerChips ?? [],
              templateChunks: turn.answerTemplateChunks,
            },
          });
          liveAnswerIds.add(turn.id);
        } else {
          entries.push({ kind: 'instruction' });
        }
      } else if (turn.kind === 'plan_proposed') {
        for (const answer of turn.answeredQuestions) {
          entries.push({ kind: 'answer', answer });
          liveAnswerIds.add(answer.id);
        }
      }
    }
    return new Map(
      [...groupQuestionAnswers(entries)].map(([id, answers]) => [
        id,
        <div
          key={id}
          className={`${styles.answerSummaryTurn} ${liveAnswerIds.has(id) ? styles.answerEntering : ''}`}
        >
          <QuestionAnswerSummary answers={answers} nodes={nodes} />
        </div>,
      ])
    );
  }, [historyTurns, liveTurns, nodes]);

  const historyBubbles = useMemo(
    () =>
      historyTurns.map((turn) => {
        if (turn.answer_to_question)
          return answerSummaries.get(turn.id) ?? null;
        const presentation =
          turn.role === 'assistant' ? turn.v2_outcome?.presentation : undefined;
        if (presentation) {
          const restoredNodes = presentation.node_ids.map((nodeId) =>
            nodes.find((node) => node.id === nodeId)
          );
          if (
            restoredNodes.length === presentation.node_ids.length &&
            restoredNodes.every(
              (node): node is ReactFlowNode => node !== undefined
            )
          ) {
            const graph = { nodes, edges };
            return (
              <div
                key={turn.id}
                className={`${styles.turn} ${styles.turnAssistant} ${styles.planTurn}`}
              >
                <div className={styles.planIntro}>
                  <span>Got it. Here’s what I’ll draft: </span>
                  <span>{presentation.intent_summary}</span>
                </div>
                {restoredNodes.map((node) => {
                  const description = describeExistingNode(node, graph);
                  return (
                    <div
                      key={node.id}
                      className={styles.planStep}
                      data-testid='restored-plan-step'
                    >
                      <button
                        type='button'
                        className={styles.planStepMain}
                        disabled={isSending || Boolean(activeDraftId)}
                        onClick={() => onOpenNodeConfiguration(node.id)}
                      >
                        <span className={styles.planStepCheck}>
                          <Check size={12} />
                        </span>
                        <span className={styles.planStepLabel}>
                          <span className={styles.planStepTitle}>
                            {description.title}
                          </span>
                          <span
                            className={styles.planStepValue}
                            aria-hidden={description.value ? undefined : true}
                          >
                            {description.value}
                          </span>
                        </span>
                      </button>
                      <button
                        type='button'
                        className={styles.planCheckpointRevert}
                        disabled
                        title='No longer available'
                        aria-label='Undo step unavailable after reload'
                      >
                        <Undo2 size={13} />
                      </button>
                    </div>
                  );
                })}
              </div>
            );
          }
        }
        if (
          turn.role === 'assistant' &&
          turn.v2_outcome?.draft_status === 'awaiting_answer' &&
          turn.v2_outcome.question
        ) {
          return null;
        }

        return (
          <div
            key={turn.id}
            className={`${styles.turn} ${turn.role === 'user' ? styles.turnUser : styles.turnAssistant}`}
          >
            <div
              className={`${styles.bubble} ${
                turn.role === 'user'
                  ? styles.bubbleUser
                  : styles.bubbleAssistant
              }`}
            >
              {turn.attachment && (
                <div className={styles.messageAttachmentChip}>
                  <Paperclip size={12} />
                  {turn.attachment.filename}
                </div>
              )}
              {turn.content}
            </div>
          </div>
        );
      }),
    [
      activeDraftId,
      answerSummaries,
      edges,
      historyTurns,
      isSending,
      nodes,
      onOpenNodeConfiguration,
    ]
  );

  // Generate opens this panel before an unsaved draft has received its id.
  // Keep the submitted prompt visible across workflow persistence and chat
  // session startup, then swap it for the identical live user turn once
  // handleSend runs. This avoids flashing the manual-unsaved warning or the
  // generic empty state during the intentional startup path.
  const optimisticInitialMessage =
    initialMessage && historyTurns.length === 0 && liveTurns.length === 0 ? (
      <div
        data-testid='optimistic-initial-message'
        className={`${styles.turn} ${styles.turnUser} ${styles.promptEntering}`}
        // eslint-disable-next-line react/forbid-dom-props -- Continue the measured entrance clock across optimistic/live mounts.
        style={initialPromptAnimationStyle}
      >
        <div className={`${styles.bubble} ${styles.bubbleUser}`}>
          {initialMessage}
        </div>
      </div>
    ) : null;

  let latestPlanTurnIndex = -1;
  for (let index = liveTurns.length - 1; index >= 0; index -= 1) {
    const turn = liveTurns[index];
    if (turn.role === 'assistant' && turn.kind === 'plan_proposed') {
      latestPlanTurnIndex = index;
      break;
    }
  }
  const hasStandalonePromptAfterLatestPlan = liveTurns
    .slice(latestPlanTurnIndex + 1)
    .some((turn) => turn.role === 'user' && !turn.answerQuestionPrompt);
  const questionForDisplay = departingQuestion?.question ?? displayedQuestion;
  let planScopedStreamingTurnIndex = -1;
  if (latestPlanTurnIndex >= 0 && !hasStandalonePromptAfterLatestPlan) {
    for (
      let index = liveTurns.length - 1;
      index > latestPlanTurnIndex;
      index -= 1
    ) {
      const turn = liveTurns[index];
      if (turn.role === 'assistant' && turn.kind === 'streaming') {
        planScopedStreamingTurnIndex = index;
        break;
      }
    }
  }

  const renderPendingQuestion = () =>
    questionForDisplay ? (
      <ClarifyingQuestionCard
        key={questionForDisplay.question_id}
        question={questionForDisplay}
        initialAnswer={
          recoveredAnswer?.questionId === questionForDisplay.question_id
            ? recoveredAnswer
            : undefined
        }
        disabled={isSending || departingQuestion !== null}
        exiting={departingQuestion !== null}
        onSubmit={(values, templateChunks) => {
          const _ = handleSubmitAnswer(values, templateChunks);
        }}
      />
    ) : null;

  if (!open) return null;

  const renderedGraphBytes = currentSerializedData(nodes, edges);

  return (
    <div className={styles.panel} ref={ref}>
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <AIHistoryPageIcon color='#111827' size={18} />
          <span>Co-pilot</span>
        </div>
        <button
          onClick={handleClosePanel}
          className={styles.closeButton}
          title='Close'
          aria-label='Close Copilot'
        >
          <X size={18} />
        </button>
      </div>

      {!workflowId && !initialMessage && (
        <Alert
          type='info'
          showIcon
          message='Add a trigger to this workflow first. Copilot needs a saved draft to work with.'
          className={styles.startError}
        />
      )}

      {!workflowId && optimisticInitialMessage && (
        <div className={styles.messages}>
          {optimisticInitialMessage}
          <div className={styles.draftingRow}>
            <span className={styles.draftingDot} />
            <span className={styles.draftingLabel}>Starting Copilot...</span>
          </div>
        </div>
      )}

      {workflowId && startError && (
        <Alert
          type='error'
          showIcon
          message={startError}
          className={styles.startError}
        />
      )}

      {workflowId && retiredDraftNotice && (
        <Alert
          type='warning'
          showIcon
          message={retiredDraftNotice}
          className={styles.startError}
        />
      )}

      {workerUpdating && (
        <div className={styles.segmentRecoveryCard} role='alert'>
          <strong>Copilot is updating</strong>
          <span>
            Your draft and answer are preserved. Retry when the server update
            finishes.
          </span>
          <Button
            size='small'
            disabled={isSending || isStarting}
            onClick={() => workerRetryRef.current?.()}
          >
            Retry
          </Button>
        </div>
      )}

      {workflowId && isStarting && !startError && (
        <div className={styles.messages}>
          {optimisticInitialMessage}
          <div className={styles.draftingRow}>
            <Spin size='small' /> <span>Starting Copilot...</span>
          </div>
        </div>
      )}

      {workflowId && !isStarting && !startError && (
        <div className={styles.messages}>
          {historyTurns.length === 0 &&
            liveTurns.length === 0 &&
            !draftRecoveryStatus &&
            !optimisticInitialMessage && (
              <div className={styles.emptyState}>
                Describe the automation you want, e.g. &quot;When a
                shipment&apos;s battery is low, send a Slack message to the ops
                channel.&quot;
              </div>
            )}
          {optimisticInitialMessage}
          {historyBubbles}
          {liveTurns.map((turn, i) => {
            if (i === planScopedStreamingTurnIndex) return null;
            if (turn.role === 'user') {
              if (turn.answerQuestionPrompt)
                return answerSummaries.get(turn.id) ?? null;
              return (
                <div
                  key={turn.id}
                  className={`${styles.turn} ${styles.turnUser} ${turn.animateIn ? styles.promptEntering : ''}`}
                  // eslint-disable-next-line react/forbid-dom-props -- Continue the measured entrance clock across optimistic/live mounts.
                  style={
                    i === 0 && turn.content === initialMessage
                      ? initialPromptAnimationStyle
                      : undefined
                  }
                >
                  <div className={`${styles.bubble} ${styles.bubbleUser}`}>
                    {turn.attachment && (
                      <div className={styles.messageAttachmentChip}>
                        <Paperclip size={12} />
                        {turn.attachment.filename}
                      </div>
                    )}
                    {turn.content}
                  </div>
                </div>
              );
            }
            if (turn.kind === 'streaming') {
              return (
                <div
                  key={turn.id}
                  className={`${styles.turn} ${styles.turnAssistant}`}
                >
                  <div className={styles.draftingRow}>
                    <span className={styles.draftingDot} />
                    <span className={styles.draftingLabel}>Drafting...</span>
                  </div>
                </div>
              );
            }
            if (turn.kind !== 'plan_proposed') {
              if (turn.kind === 'acknowledgement') {
                if (turn.visible === false) return null;
                return (
                  <div
                    key={turn.id}
                    className={`${styles.turn} ${styles.turnAssistant} ${styles.acknowledgementTurn} ${turn.animateIn ? styles.acknowledgementEntering : ''}`}
                  >
                    <div className={styles.planIntro}>
                      <span>Got it. Here’s what I’ll draft: </span>
                      <span>{turn.content}</span>
                    </div>
                  </div>
                );
              }
              if (
                turn.kind === 'clarifying_question' &&
                turn.content === null
              ) {
                return null;
              }
              return (
                <div
                  key={turn.id}
                  className={`${styles.turn} ${styles.turnAssistant}`}
                >
                  <div
                    className={`${styles.bubble} ${styles.bubbleAssistant} ${
                      turn.kind === 'error' ? styles.bubbleError : ''
                    }`}
                  >
                    {turn.content}
                  </div>
                </div>
              );
            }
            return (
              <div
                key={turn.id}
                className={`${styles.turn} ${styles.turnAssistant} ${styles.planTurn}`}
              >
                {turn.showIntro && (
                  <div className={styles.planIntro}>
                    <span>Got it. Here’s what I’ll draft: </span>
                    <span>{turn.intro}</span>
                  </div>
                )}
                {turn.rows.map((row, ri) => {
                  if (!turn.isComplete && !row.completed) return null;
                  const turnStillOwnsCurrentGraph = [
                    turn.baseCheckpointId,
                    ...turn.rows.map(
                      (candidateRow) => candidateRow.checkpointId
                    ),
                  ]
                    .filter((id): id is string => Boolean(id))
                    .some((checkpointId) => {
                      const checkpoint = getCheckpoint(checkpointId);
                      return (
                        checkpoint !== null &&
                        currentSerializedData(
                          checkpoint.nodes,
                          checkpoint.edges
                        ) === renderedGraphBytes
                      );
                    });
                  const targetCheckpointId = row.completed
                    ? ri === 0
                      ? turn.baseCheckpointId
                      : turn.rows[ri - 1]?.checkpointId
                    : row.checkpointId;
                  const targetMeta = targetCheckpointId
                    ? checkpoints.find((item) => item.id === targetCheckpointId)
                    : undefined;
                  return (
                    <div
                      key={row.id}
                      className={`${styles.planStep} ${!turn.isComplete ? styles.planStepEntering : ''}`}
                    >
                      <button
                        type='button'
                        className={styles.planStepMain}
                        disabled={
                          !row.nodeId ||
                          isRevealing ||
                          isSending ||
                          Boolean(activeDraftId)
                        }
                        onClick={() =>
                          row.nodeId && onOpenNodeConfiguration(row.nodeId)
                        }
                      >
                        <span
                          className={
                            row.completed
                              ? styles.planStepCheck
                              : styles.planStepPending
                          }
                        >
                          {row.completed && <Check size={12} />}
                        </span>
                        <span
                          className={
                            row.completed
                              ? styles.planStepLabel
                              : `${styles.planStepLabel} ${styles.planStepLabelPending}`
                          }
                        >
                          <span className={styles.planStepTitle}>
                            {row.title}
                          </span>
                          <span
                            key={row.valueRevealKey ?? 'empty-value'}
                            className={`${styles.planStepValue} ${row.valueRevealKey ? styles.planStepValueRevealing : ''}`}
                            // eslint-disable-next-line react/forbid-dom-props -- Each compiled value carries its own staggered reveal delay.
                            style={
                              row.valueRevealDelayMs === undefined
                                ? undefined
                                : ({
                                    '--plan-value-reveal-delay': `${row.valueRevealDelayMs}ms`,
                                  } as CSSProperties)
                            }
                            aria-hidden={row.value ? undefined : true}
                          >
                            {row.value}
                          </span>
                        </span>
                      </button>
                      <button
                        type='button'
                        className={styles.planCheckpointRevert}
                        disabled={
                          !targetCheckpointId ||
                          targetMeta?.evicted ||
                          !turnStillOwnsCurrentGraph ||
                          isSending ||
                          Boolean(activeDraftId)
                        }
                        onClick={() => handleToggleStep(turn.id, ri)}
                        title={
                          targetMeta?.evicted
                            ? 'No longer available'
                            : !turnStillOwnsCurrentGraph
                              ? 'Workflow changed since this Copilot edit'
                              : undefined
                        }
                        aria-label={row.completed ? 'Undo step' : 'Redo step'}
                      >
                        {row.completed ? (
                          <Undo2 size={13} />
                        ) : (
                          <Redo2 size={13} />
                        )}
                      </button>
                    </div>
                  );
                })}
                {turn.answeredQuestions.map(
                  (answer) => answerSummaries.get(answer.id) ?? null
                )}
                {(planScopedStreamingTurnIndex >= 0 ||
                  !turn.isComplete ||
                  turn.statusLabel) && (
                  <div className={styles.draftingRow}>
                    <span className={styles.draftingDot} />
                    <span className={styles.draftingLabel}>
                      {turn.statusLabel ?? 'Drafting...'}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
          {draftRecoveryStatus && (
            <div className={styles.segmentRecoveryCard} role='alert'>
              <strong>
                {draftRecoveryStatus.terminalStatus === 'recovery_conflict'
                  ? 'Copilot could not safely resume this draft'
                  : draftRecoveryStatus.terminalStatus === 'discard_failed'
                    ? 'The Copilot draft could not be discarded'
                    : 'Copilot stopped before configuration finished'}
              </strong>
              <span>
                {draftRecoveryStatus.terminalStatus === 'recovery_conflict'
                  ? 'The workflow changed before Copilot could confirm its saved graph. Review the canvas manually.'
                  : 'Your saved workflow remains unchanged. Review it manually or reload before trying again.'}
              </span>
              <div className={styles.segmentRecoveryActions}>
                {discardCapability && (
                  <Button
                    size='small'
                    danger
                    disabled={isSending}
                    onClick={() => {
                      const _ = handleDiscardDraft();
                    }}
                  >
                    Discard Copilot draft
                  </Button>
                )}
                <Button size='small' onClick={handleReloadWorkflow}>
                  Reload workflow
                </Button>
              </div>
            </div>
          )}
          {!draftRecoveryStatus &&
            activeDraftId &&
            !pendingQuestion &&
            !isSending &&
            !segmentHandshakeState && (
              <div className={styles.segmentRecoveryCard} role='status'>
                <strong>Copilot drafting is paused</strong>
                <span>
                  Stop drafting to unlock the canvas and review the saved
                  workflow manually.
                </span>
                <div className={styles.segmentRecoveryActions}>
                  <Button size='small' onClick={handleStopDrafting}>
                    Stop drafting
                  </Button>
                </div>
              </div>
            )}
          {segmentHandshakeState &&
            segmentHandshakeState !== 'receipt_error' && (
              <div className={styles.segmentHandshakeStatus}>
                <Spin size='small' />
                <span>
                  {segmentHandshakeState === 'persisting_segment'
                    ? 'Saving drafted steps...'
                    : 'Confirming drafted steps...'}
                </span>
              </div>
            )}
          {segmentHandshakeState === 'receipt_error' && segmentRecovery && (
            <div className={styles.segmentRecoveryCard} role='alert'>
              <strong>
                {segmentRecovery.terminalCode
                  ? 'This workflow changed elsewhere'
                  : segmentRecovery.compatibilityError
                    ? 'Copilot is updating'
                    : "Couldn't save this draft segment"}
              </strong>
              <span>
                {segmentRecovery.terminalCode
                  ? 'Reload the workflow before continuing.'
                  : segmentRecovery.compatibilityError
                    ? 'Your draft is preserved. Retry when the server update finishes.'
                    : 'The canvas will stay locked until you retry or stop.'}
              </span>
              <div className={styles.segmentRecoveryActions}>
                {segmentRecovery.terminalCode ? (
                  <Button size='small' onClick={handleReloadWorkflow}>
                    Reload workflow
                  </Button>
                ) : (
                  <>
                    <Button
                      size='small'
                      onClick={() => {
                        const _ = handleRetrySegment();
                      }}
                    >
                      Retry
                    </Button>
                    <Button size='small' onClick={handleStopDrafting}>
                      Stop drafting
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
          {workflowConflict && (
            <div className={styles.segmentRecoveryCard} role='alert'>
              <strong>This workflow changed elsewhere</strong>
              <span>Reload the workflow before continuing.</span>
              <div className={styles.segmentRecoveryActions}>
                <Button size='small' onClick={handleReloadWorkflow}>
                  Reload workflow
                </Button>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      )}

      {workflowId &&
        !startError &&
        !questionForDisplay &&
        !isSubmittingAnswer &&
        attachedFile && (
          <div className={styles.attachChip}>
            <Paperclip size={13} />
            <span className={styles.attachChipName}>
              {attachedFile.file.name}
            </span>
            <button
              type='button'
              className={styles.attachChipRemove}
              onClick={handleRemoveAttachment}
              aria-label='Remove attachment'
            >
              <X size={13} />
            </button>
          </div>
        )}
      {workflowId &&
        !startError &&
        ((!questionForDisplay && !isSubmittingAnswer) ||
          attachError === STOP_RECONCILIATION_ERROR) &&
        attachError && <div className={styles.attachError}>{attachError}</div>}

      {workflowId && !startError && (
        <div
          className={`${styles.inputRow} ${questionForDisplay || isSubmittingAnswer ? styles.inputRowWithQuestion : ''}`}
        >
          {questionForDisplay ? (
            <div
              className={styles.questionDock}
              data-testid='copilot-question-dock'
            >
              {renderPendingQuestion()}
            </div>
          ) : isSubmittingAnswer ? (
            <div className={styles.answerPending} role='status'>
              <Spin size='small' />
              <span>Preparing the next step...</span>
            </div>
          ) : (
            <>
              <input
                ref={fileInputRef}
                type='file'
                accept={ATTACHMENT_ACCEPT}
                hidden
                onChange={(e) => {
                  const _ = handleFileSelected(e);
                }}
              />
              <div className={styles.inputShell} data-testid='copilot-composer'>
                <Input.TextArea
                  className={styles.composerTextArea}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onPressEnter={(e) => {
                    if (!e.shiftKey) {
                      e.preventDefault();
                      const _ = handleSend();
                    }
                  }}
                  placeholder='Describe the workflow you want to build...'
                  autoSize={false}
                  disabled={
                    isStarting ||
                    !sessionId ||
                    isSending ||
                    workflowConflict ||
                    draftRecoveryStatus !== null
                  }
                />
                <div className={styles.inputActions}>
                  <Button
                    type='text'
                    className={styles.attachButton}
                    icon={<Paperclip size={17} />}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={
                      isStarting ||
                      !sessionId ||
                      isSending ||
                      workflowConflict ||
                      draftRecoveryStatus !== null ||
                      Boolean(pendingQuestion)
                    }
                    aria-label='Attach a file'
                  />
                  <Button
                    type='text'
                    className={styles.sendButton}
                    icon={<FilledSendIcon />}
                    onClick={() => {
                      const _ = handleSend();
                    }}
                    disabled={
                      isStarting ||
                      !sessionId ||
                      isSending ||
                      isRevealing ||
                      workflowConflict ||
                      draftRecoveryStatus !== null ||
                      (!inputValue.trim() && !attachedFile)
                    }
                    loading={isSending && !isRevealing}
                    aria-label='Send message'
                  />
                </div>
              </div>
            </>
          )}
          <span className={styles.composerDisclaimer}>
            The AI Copilot can make mistakes. Verify important information.
          </span>
        </div>
      )}
    </div>
  );
});
